// Bills and payments (ERP-PLAN §5.9), shared by the screens, the server, the MCP and tests, so every number agrees:
// statuses and labels, the mutations' field schemas, what's paid and what's due on a bill, the deposit and balance
// of a PO, the 3-way match (PO lines × received × billed), the Payments due board, the Wise reference, matching Wise
// history to bills and POs, and reading a Wise statement CSV (never its account numbers).
//
// Money is in the bill's currency, worked out on scaled integers (shared/po.ts mulRound), to the cent.

import { z } from "zod";
import { toCsv } from "./csv.ts";
import { addDays } from "./day.ts";
import type {
  Attachment,
  Bill,
  BillLine,
  MakeOrder,
  Payment,
  PaymentAllocation,
  Po,
  PoLine,
  ProductionCost,
  ProductionRun,
  Receipt,
  ToolingItem,
  Vendor,
  VendorRecipient,
  WiseTransfer,
} from "./db.gen.ts";
import { paidWithText } from "./pay-accounts.ts";
import { mulRound, sumMoney } from "./po.ts";
import type { PresalePack } from "./presales.ts";
import { backersDelivered } from "./receiving.ts";

/** The money tables: never synced to warehouse receivers (server/src/sync.ts leaves them out for that role). */
export const MONEY_TABLES = [
  "bill",
  "bill_line",
  "payment",
  "payment_allocation",
  "pay_account", // Which card --- HHL's own ways to pay
  "vendor_recipient",
  "wise_batch",
  "wise_connection",
  "wise_event",
  "wise_transfer",
  // Pre-sales --- campaigns, their reservations, income and shipments to backers (pledge values)
  "presale",
  "presale_line",
  "presale_income",
  "presale_shipment",
  "presale_shipment_line",
] as const;
/** Attachments of these owners are money too (a bill's invoice, a payment's receipt): never synced or shown to receivers. */
export const MONEY_ATTACHMENT_OWNERS = ["bill", "payment", "wise_transfer", "presale_income"] as const;
/** Pre-sales --- run cost rows that are money a receiver never sees: shipping to backers. */
export const MONEY_COST_CATEGORIES = ["backer_shipping"] as const;
/** Whether a receiver may not see this record (the History tab, the sync stream): a money table, or a money attachment. */
export const isMoneyRecord = (table: string, row?: { owner_type?: unknown; category?: unknown; cost_category?: unknown } | null) =>
  (MONEY_TABLES as readonly string[]).includes(table) ||
  (table === "attachment" && !!row && (MONEY_ATTACHMENT_OWNERS as readonly string[]).includes(String(row.owner_type))) ||
  // Pre-sales --- a file on a run cost that's shipping to backers (`cost_category`: the owner's category)
  (table === "attachment" && !!row && (MONEY_COST_CATEGORIES as readonly string[]).includes(String(row.cost_category))) ||
  (table === "production_cost" && !!row && (MONEY_COST_CATEGORIES as readonly string[]).includes(String(row.category)));

// Statuses ----------------------------------------------------------------------------------------------------

export const BILL_STATUSES = ["draft", "approved", "scheduled", "paid", "void"] as const;
export type BillStatus = (typeof BILL_STATUSES)[number];
export const BILL_STATUS_LABELS: Record<BillStatus, string> = {
  draft: "Draft",
  approved: "Approved",
  scheduled: "Scheduled",
  paid: "Paid",
  void: "Void",
};
export const BILL_STATUS_HINTS: Record<BillStatus, string> = {
  draft: "Entered, not checked yet: approve it to put it on the Payments due board",
  approved: "Checked and owed: waiting to be paid",
  scheduled: "A payment is on its way (a Wise transfer waiting to be funded, or being sent)",
  paid: "Paid in full",
  void: "Cancelled: not owed",
};
export const BILL_STATUS_TONE = { draft: "neutral", approved: "accent", scheduled: "warn", paid: "ok", void: "neutral" } as const;

export const BILL_KINDS = ["deposit", "balance", "full", "freight", "duty", "tooling", "other"] as const;
export type BillKind = (typeof BILL_KINDS)[number];
export const BILL_KIND_LABELS: Record<BillKind, string> = {
  deposit: "Deposit",
  balance: "Balance",
  full: "Full invoice",
  freight: "Freight",
  duty: "Duty / tariff",
  tooling: "Tooling",
  other: "Other",
};

/** `rounding`: the vendor took a little less (or more) than the documents say; up to ROUNDING_MAX it's accounted for. */
export const BILL_LINE_KINDS = ["goods", "deposit", "deposit_credit", "charge", "other", "rounding"] as const;
export type BillLineKind = (typeof BILL_LINE_KINDS)[number];
export const BILL_LINE_KIND_LABELS: Record<BillLineKind, string> = {
  goods: "Goods",
  deposit: "Deposit",
  deposit_credit: "Less deposit",
  charge: "Charge",
  other: "Other",
  rounding: "Vendor rounding",
};

export const PAYMENT_METHODS = ["wise", "card", "wire", "other"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = { wise: "Wise", card: "Card", wire: "Wire", other: "Other" };
/** Ways to record a payment made outside HHL Ops (a Wise transfer made in the Wise app goes through Wise history). */
export const OUTSIDE_METHODS = ["card", "wire", "other"] as const;

export const PAYMENT_STATUSES = ["draft", "awaiting_funding", "processing", "paid", "cancelled", "refunded", "failed"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];
export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  draft: "Not in Wise yet",
  awaiting_funding: "Approve and fund in Wise",
  processing: "Wise is sending it",
  paid: "Paid",
  cancelled: "Cancelled",
  refunded: "Refunded",
  failed: "Wise refused it",
};
export const PAYMENT_STATUS_TONE = {
  draft: "warn",
  awaiting_funding: "warn",
  processing: "accent",
  paid: "ok",
  cancelled: "neutral",
  refunded: "danger",
  failed: "danger",
} as const;
/** A payment still on its way: it holds the bill's money (scheduled) until it's paid or stopped. */
export const OPEN_PAYMENT: ReadonlySet<PaymentStatus> = new Set(["draft", "awaiting_funding", "processing"]);

/** Wise's transfer statuses in plain words (Wise's own suggested wording). */
export const WISE_STATE_LABELS: Record<string, string> = {
  incoming_payment_waiting: "Waiting for funding",
  incoming_payment_initiated: "Funding on its way to Wise",
  processing: "Processing",
  funds_converted: "Processing",
  outgoing_payment_sent: "Sent to the vendor",
  cancelled: "Cancelled",
  funds_refunded: "Refunded",
  bounced_back: "Bounced back",
  charged_back: "Funds returned",
  unknown: "Status unknown",
};

/** A payment's status from Wise's transfer state. */
export function statusForWiseState(state: string): PaymentStatus {
  switch (state) {
    case "incoming_payment_waiting":
      return "awaiting_funding";
    case "incoming_payment_initiated":
    case "processing":
    case "funds_converted":
    case "bounced_back": // it'll be delivered late, or refunded
    case "unknown":
      return "processing";
    case "outgoing_payment_sent":
      return "paid";
    case "cancelled":
      return "cancelled";
    case "funds_refunded":
    case "charged_back":
      return "refunded";
    default:
      return "processing";
  }
}

/** Wise's "active cases" on a transfer, in words (anything else shows as Wise names it). */
export const WISE_CASE_LABELS: Record<string, string> = {
  deposit_amount_less_invoice: "The funding was less than the transfer",
  deposit_amount_more_invoice: "The funding was more than the transfer",
  invalid_beneficiary: "Wise says the recipient's details are wrong",
  invalid_beneficiary_details: "Wise says the recipient's details are wrong",
  refund_requested: "A refund was asked for",
  verification_required: "Wise needs a verification",
};
export const caseText = (c: string) => WISE_CASE_LABELS[c] ?? c.replaceAll("_", " ");

// Fields --------------------------------------------------------------------------------------------------------

const text = (max = 200) => z.string().trim().max(max);
const isoDate = z.iso.date("Use a date like 2026-12-31").nullable();
const currency = z.string().trim().toUpperCase().length(3, "Use a 3-letter currency code");
const amount = z.number().min(0, "An amount is 0 or more").max(100_000_000);
const lineAmount = z.number().min(-100_000_000).max(100_000_000);

export const billFields = z.object({
  vendor_id: z.uuid(),
  kind: z.enum(BILL_KINDS),
  invoice_number: text(100),
  bill_date: isoDate,
  due_date: isoDate,
  currency,
  amount,
  description: text(500),
  notes: text(5000),
});

export const billLineInput = z.object({
  id: z.uuid().optional(),
  kind: z.enum(BILL_LINE_KINDS).default("goods"),
  description: text(500).default(""),
  po_id: z.uuid().nullable().optional(),
  po_line_id: z.uuid().nullable().optional(),
  receipt_id: z.uuid().nullable().optional(),
  production_cost_id: z.uuid().nullable().optional(),
  tooling_item_id: z.uuid().nullable().optional(),
  credit_bill_id: z.uuid().nullable().optional(),
  qty: z.number().min(-10_000_000).max(10_000_000).nullable().optional(),
  unit_price: z.number().min(0).max(100_000_000).nullable().optional(),
  amount: lineAmount,
});
export type BillLineInput = z.input<typeof billLineInput>;

export const billLineFields = z.object({
  description: text(500),
  qty: z.number().min(-10_000_000).max(10_000_000).nullable(),
  unit_price: z.number().min(0).max(100_000_000).nullable(),
  amount: lineAmount,
  production_cost_id: z.uuid().nullable(),
  tooling_item_id: z.uuid().nullable(),
  receipt_id: z.uuid().nullable(),
});

/** Parts of a PO a bill can be made for. */
export const PO_BILL_PARTS = ["deposit", "balance", "received"] as const;
export type PoBillPart = (typeof PO_BILL_PARTS)[number];

// Data ----------------------------------------------------------------------------------------------------------

type PoRow = Pick<
  Po,
  | "id"
  | "number"
  | "vendor_id"
  | "kind"
  | "status"
  | "currency"
  | "deposit_pct"
  | "expected_ship"
  | "vendor_ref"
  | "production_run_id"
  | "acknowledged_at"
  | "acknowledged_on"
  | "dated_on"
  | "sent_on"
  | "created_at"
  | "historical"
>;
/**
 * Pre-sales --- qty_direct: a make PO line's units that go straight from the CM to backers (shared/presales.ts);
 * qty_direct_shipped: of them, sent.
 */
type PoLineRow = Pick<PoLine, "id" | "po_id" | "position" | "item_id" | "sku" | "name" | "qty" | "unit_price" | "qty_received" | "qty_rejected"> &
  Partial<Pick<PoLine, "qty_direct" | "qty_direct_shipped">>;

/**
 * Pre-sales --- what a PO line has delivered, for the 3-way match and billing what's received: received at its
 * ship-to, plus its units delivered to backers (`backersDelivered()`: shipped to them or built for them, whichever is more,
 * up to what's left after HHL's; never just because HHL's units arrived). `output` is what its make order reports built.
 */
export function deliveredQty(pl: PoLineRow, output: number | null | undefined): { qty: number; direct: number } {
  const direct = backersDelivered({ ...pl, qty_output: output });
  return { qty: Number(pl.qty_received) + direct, direct };
}
type CostRow = Pick<
  ProductionCost,
  | "id"
  | "run_id"
  | "category"
  | "vendor_id"
  | "description"
  | "amount"
  | "currency"
  | "usd_rate"
  | "basis"
  | "invoice_number"
  | "invoice_date"
  | "po_id"
  | "paid_on"
  | "paid_method"
  | "paid_ref"
  | "external_ref"
>;
type ToolingRow = Pick<
  ToolingItem,
  | "id"
  | "project_id"
  | "name"
  | "kind"
  | "vendor_id"
  | "amount"
  | "currency"
  | "usd_rate"
  | "invoice_number"
  | "invoice_date"
  | "paid_by_run_id"
  | "paid_on"
  | "paid_method"
  | "paid_ref"
>;

/** The synced rows bills read (the browser passes its tables; server/src/bills.ts queries the same with to_jsonb). */
export type BillData = {
  /** Which card --- HHL's payment accounts, for "Amex ••1005 · the vendor's website" (migration 073). */
  payAccounts?: readonly { id: string; name: string; last4: string }[];
  bills: readonly Bill[];
  billLines: readonly BillLine[];
  payments: readonly Payment[];
  allocations: readonly PaymentAllocation[];
  vendors: readonly Pick<Vendor, "id" | "name" | "short_name" | "aliases" | "currency" | "deposit_pct" | "archived">[];
  recipients: readonly VendorRecipient[];
  pos: readonly PoRow[];
  poLines: readonly PoLineRow[];
  receipts: readonly Pick<Receipt, "id" | "po_id" | "number" | "received_at">[];
  costs: readonly CostRow[];
  tooling: readonly ToolingRow[];
  runs: readonly Pick<ProductionRun, "id" | "number" | "name" | "project_id" | "status">[];
  attachments: readonly Pick<Attachment, "id" | "owner_type" | "owner_id" | "kind" | "filename" | "content_type" | "doc_date" | "created_at">[];
  /** Pre-sales --- what each make order reports built, for the backers' units a make PO line has delivered. */
  makeOrders?: readonly Pick<MakeOrder, "po_line_id" | "qty_output">[];
};

function group<T, K>(rows: readonly T[], key: (r: T) => K | null | undefined): Map<K, T[]> {
  const out = new Map<K, T[]>();
  for (const r of rows) {
    const k = key(r);
    if (k == null) continue;
    const list = out.get(k);
    list ? list.push(r) : out.set(k, [r]);
  }
  return out;
}

export type BillIndex = ReturnType<typeof indexBills>;

/** Lookups for bills; build once per change of the rows. Results are cached on it. */
export function indexBills(d: BillData, today: string) {
  const live = (b: Bill | undefined) => !!b && b.status !== "void";
  const billById = new Map(d.bills.map((b) => [b.id, b]));
  const lines = [...d.billLines].sort((a, b) => a.position - b.position);
  const liveLines = lines.filter((l) => live(billById.get(l.bill_id)));
  const outputByLine = new Map<string, number>();
  for (const m of d.makeOrders ?? []) outputByLine.set(m.po_line_id, (outputByLine.get(m.po_line_id) ?? 0) + Number(m.qty_output));
  return {
    d,
    today,
    billById,
    payAccountById: new Map((d.payAccounts ?? []).map((a) => [a.id, a])), // Which card ---
    outputByLine,
    linesByBill: group(lines, (l) => l.bill_id),
    /** Lines of bills that aren't void, by what they're for. */
    linesByPo: group(liveLines, (l) => l.po_id),
    linesByPoLine: group(liveLines, (l) => l.po_line_id),
    linesByCost: group(liveLines, (l) => l.production_cost_id),
    linesByTooling: group(liveLines, (l) => l.tooling_item_id),
    paymentById: new Map(d.payments.map((p) => [p.id, p])),
    allocsByBill: group(d.allocations, (a) => a.bill_id),
    allocsByPayment: group(d.allocations, (a) => a.payment_id),
    vendorById: new Map(d.vendors.map((v) => [v.id, v])),
    recipientByVendor: new Map(d.recipients.filter((r) => r.currency === "USD").map((r) => [r.vendor_id, r])),
    vendorByRecipient: new Map(d.recipients.map((r) => [Number(r.wise_recipient_id), r.vendor_id])),
    poById: new Map(d.pos.map((p) => [p.id, p])),
    poLinesByPo: group(
      [...d.poLines].sort((a, b) => a.position - b.position),
      (l) => l.po_id,
    ),
    poLineById: new Map(d.poLines.map((l) => [l.id, l])),
    receiptsByPo: group(d.receipts, (r) => r.po_id),
    costById: new Map(d.costs.map((c) => [c.id, c])),
    toolingById: new Map(d.tooling.map((t) => [t.id, t])),
    runById: new Map(d.runs.map((r) => [r.id, r])),
    filesByOwner: group(d.attachments, (a) => `${a.owner_type}:${a.owner_id}`),
    cache: {
      po: new Map<string, PoMatch>(),
      bill: new Map<string, BillMatch>(),
      state: new Map<string, BillState>(),
      /** What Wise history can match (candidates()), worked out once per index. */
      cands: null as Candidate[] | null,
    },
  };
}

// Money helpers -------------------------------------------------------------------------------------------------

const cents = (n: number) => Math.round(mulRound(n, 100, 0));
const same = (a: number, b: number) => cents(a) === cents(b);
const add = (...xs: number[]) => sumMoney(xs);
/** "$1,234.56" or "1,234.56 CNY". */
export function moneyText(n: number, cur = "USD") {
  const s = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sign = n < 0 ? "−" : "";
  return cur.toUpperCase() === "USD" ? `${sign}$${s}` : `${sign}${s} ${cur.toUpperCase()}`;
}
const qtyText = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 3 });
const vendorShortOf = (v: Pick<Vendor, "name" | "short_name"> | undefined) => (v ? v.short_name || v.name.split(/[\s,]+/)[0] || v.name : "");
export const vendorLabelOf = (v: Pick<Vendor, "name" | "short_name"> | undefined) => (v ? v.short_name || v.name : "");

// What's paid on a bill -------------------------------------------------------------------------------------------

export type BillState = {
  amount: number;
  /** Paid by payments that went. */
  paid: number;
  /** Held by payments still on their way (a Wise transfer waiting to be funded or being sent). */
  pending: number;
  /** Still to pay: amount − paid − pending (never below 0). */
  due: number;
  /** What the status should be from its payments (draft and void stay as they are). */
  status: BillStatus;
  paidOn: string | null;
  payments: { payment: Payment; amount: number }[];
};

/** A bill's payments: what's paid, what's on its way, what's left, and the status that follows. */
export function billState(ix: BillIndex, billId: string): BillState {
  const hit = ix.cache.state.get(billId);
  if (hit) return hit;
  const bill = ix.billById.get(billId) as Bill;
  const payments = (ix.allocsByBill.get(billId) ?? [])
    .map((a) => ({ payment: ix.paymentById.get(a.payment_id) as Payment, amount: Number(a.amount) }))
    .filter((p) => p.payment);
  const st = deriveBillState(
    bill,
    payments.map((p) => ({ status: p.payment.status, amount: p.amount, paid_on: p.payment.paid_on })),
  );
  const out = { ...st, payments };
  ix.cache.state.set(billId, out);
  return out;
}

/** The rule settleBill() (server) and the screens share: a bill is paid once paid payments cover it. */
export function deriveBillState(
  bill: Pick<Bill, "amount" | "status">,
  payments: { status: PaymentStatus; amount: number; paid_on: string | null }[],
): Omit<BillState, "payments"> {
  const amount = Number(bill.amount);
  const paid = add(...payments.filter((p) => p.status === "paid").map((p) => p.amount));
  const pending = add(...payments.filter((p) => OPEN_PAYMENT.has(p.status)).map((p) => p.amount));
  const due = Math.max(0, mulRound(amount - paid - pending, 1, 2));
  const paidOn =
    payments
      .filter((p) => p.status === "paid" && p.paid_on)
      .map((p) => String(p.paid_on).slice(0, 10))
      .sort()
      .at(-1) ?? null;
  let status: BillStatus = bill.status;
  if (bill.status !== "draft" && bill.status !== "void") {
    if (amount > 0 && cents(paid) >= cents(amount)) status = "paid";
    else if (pending > 0 && cents(paid + pending) >= cents(amount)) status = "scheduled";
    else status = "approved";
  }
  return { amount, paid, pending, due, status, paidOn: status === "paid" ? paidOn : null };
}

/** The POs, runs, costs and tooling a bill is for. */
export function billLinks(ix: BillIndex, billId: string) {
  const lines = ix.linesByBill.get(billId) ?? [];
  const poIds = [...new Set(lines.map((l) => l.po_id).filter((x): x is string => !!x))];
  const costIds = [...new Set(lines.map((l) => l.production_cost_id).filter((x): x is string => !!x))];
  const toolingIds = [...new Set(lines.map((l) => l.tooling_item_id).filter((x): x is string => !!x))];
  const runShare = new Map<string, number>();
  const addRun = (run: string | null | undefined, amt: number) => run && runShare.set(run, add(runShare.get(run) ?? 0, Math.abs(amt)));
  for (const l of lines) {
    const amt = Number(l.amount);
    if (l.production_cost_id) addRun(ix.costById.get(l.production_cost_id)?.run_id, amt);
    else if (l.tooling_item_id) addRun(ix.toolingById.get(l.tooling_item_id)?.paid_by_run_id, amt);
    else if (l.po_id) addRun(ix.poById.get(l.po_id)?.production_run_id, amt);
  }
  const runIds = [...runShare.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
  return { poIds, costIds, toolingIds, runIds, runShare };
}

/** The run a bill's Wise fee goes on: the one carrying most of it (null if none). */
export const feeRunOf = (ix: BillIndex, billIds: string[]) => {
  const share = new Map<string, number>();
  for (const id of billIds) for (const [run, amt] of billLinks(ix, id).runShare) share.set(run, add(share.get(run) ?? 0, amt));
  return [...share.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
};

// Deposits and balances -------------------------------------------------------------------------------------------

/** The day a PO was acknowledged: the date on paper, else when it was recorded (HHL's date). */
export const ackDay = (po: Pick<Po, "acknowledged_on" | "acknowledged_at">, dayOf: (ts: string) => string) =>
  po.acknowledged_on ? String(po.acknowledged_on).slice(0, 10) : po.acknowledged_at ? dayOf(po.acknowledged_at) : null;

/** The PO's total, its lines priced (lines without a price count 0, and are counted). */
export function poTotal(ix: BillIndex, poId: string) {
  const lines = ix.poLinesByPo.get(poId) ?? [];
  return {
    total: sumMoney(lines.map((l) => (l.unit_price == null ? 0 : mulRound(Number(l.qty), Number(l.unit_price), 2)))),
    unpriced: lines.filter((l) => l.unit_price == null).length,
  };
}

export type PartState = "none" | "not_billed" | "billed" | "scheduled" | "paid";
export type PoPart = {
  amount: number;
  billed: number;
  paid: number;
  state: PartState;
  due: string | null;
  dueText: string;
  bills: Bill[];
};
export type PoBillPlan = {
  po: PoRow;
  total: number;
  unpriced: number;
  depositPct: number;
  deposit: PoPart;
  balance: PoPart;
  /** Lines received but not billed yet (qty × PO price). */
  receivedUnbilled: number;
  /** The PO's total less everything billed against it (deposits and credits netted). */
  unbilled: number;
  bills: Bill[];
};

const partState = (ix: BillIndex, bills: Bill[], amount: number, billed: number): PartState => {
  if (amount <= 0 && !bills.length) return "none";
  if (!bills.length) return "not_billed";
  const states = bills.map((b) => billState(ix, b.id).status);
  if (states.every((s) => s === "paid") && cents(billed) >= cents(amount)) return "paid";
  if (states.some((s) => s === "scheduled" || s === "paid")) return "scheduled";
  return "billed";
};

/**
 * A PO's money: its deposit (deposit_pct, due when the vendor acknowledges) and balance (the rest, due before it
 * ships), what's billed and paid of each, and what's still unbilled. `dayOf` turns a timestamp into HHL's date.
 */
export function poBillPlan(ix: BillIndex, poId: string, dayOf: (ts: string) => string): PoBillPlan | null {
  const po = ix.poById.get(poId);
  if (!po) return null;
  const { total, unpriced } = poTotal(ix, poId);
  const pct = Number(po.deposit_pct) || 0;
  const depositAmount = pct > 0 ? mulRound(total, pct / 100, 2) : 0;
  const lines = ix.linesByPo.get(poId) ?? [];
  const billIds = [...new Set(lines.map((l) => l.bill_id))];
  const bills = billIds.map((id) => ix.billById.get(id) as Bill).filter(Boolean);
  const depositLines = lines.filter((l) => l.kind === "deposit");
  const depositBills = bills.filter((b) => depositLines.some((l) => l.bill_id === b.id));
  const depositBilled = add(...depositLines.map((l) => Number(l.amount)));
  // A vendor's rounding isn't billing the PO: it's what the vendor took off (or added to) the bill.
  const restLines = lines.filter((l) => l.kind !== "deposit" && l.kind !== "rounding");
  const restBills = bills.filter((b) => restLines.some((l) => l.bill_id === b.id));
  const restBilled = add(...restLines.map((l) => Number(l.amount)));
  const paidOf = (bs: Bill[], ls: BillLine[]) =>
    add(
      ...bs.map((b) => {
        const st = billState(ix, b.id);
        const mine = add(...ls.filter((l) => l.bill_id === b.id).map((l) => Number(l.amount)));
        return st.amount > 0 ? mulRound(mine, Math.min(1, st.paid / st.amount), 2) : 0;
      }),
    );
  const ack = ackDay(po, dayOf);
  const balanceAmount = mulRound(total - depositAmount, 1, 2);
  let receivedUnbilled = 0;
  for (const pl of ix.poLinesByPo.get(poId) ?? []) {
    const billedQty = add(...(ix.linesByPoLine.get(pl.id) ?? []).filter((l) => l.kind === "goods").map((l) => Number(l.qty ?? 0)));
    const extra = deliveredQty(pl, ix.outputByLine.get(pl.id)).qty - billedQty;
    if (extra > 0 && pl.unit_price != null) receivedUnbilled = add(receivedUnbilled, mulRound(extra, Number(pl.unit_price), 2));
  }
  return {
    po,
    total,
    unpriced,
    depositPct: pct,
    deposit: {
      amount: depositAmount,
      billed: depositBilled,
      paid: paidOf(depositBills, depositLines),
      state: pct > 0 ? partState(ix, depositBills, depositAmount, depositBilled) : depositBills.length ? partState(ix, depositBills, 0, depositBilled) : "none",
      due: ack,
      dueText: ack ? `Due ${ack}, when ${po.number} was acknowledged` : "Due when the vendor acknowledges the PO",
      bills: depositBills,
    },
    balance: {
      amount: balanceAmount,
      billed: restBilled,
      paid: paidOf(restBills, restLines),
      state: partState(ix, restBills, balanceAmount, restBilled),
      due: po.expected_ship ? String(po.expected_ship).slice(0, 10) : null,
      dueText: po.expected_ship ? `Due before it ships (${String(po.expected_ship).slice(0, 10)})` : "Due before it ships (no ship date yet)",
      bills: restBills,
    },
    receivedUnbilled,
    unbilled: mulRound(total - depositBilled - restBilled, 1, 2),
    bills,
  };
}

/** When a bill is due: its own date, else a deposit's PO acknowledgement, else a balance's ship date. */
export function billDue(ix: BillIndex, bill: Bill, dayOf: (ts: string) => string): { date: string | null; why: string } {
  if (bill.due_date) return { date: String(bill.due_date).slice(0, 10), why: "The due date on the bill" };
  const po = billLinks(ix, bill.id)
    .poIds.map((id) => ix.poById.get(id))
    .find(Boolean);
  if (po && bill.kind === "deposit") {
    const ack = ackDay(po, dayOf);
    return ack
      ? { date: ack, why: `A deposit: due when ${po.number} was acknowledged` }
      : { date: null, why: `A deposit: due when ${po.number} is acknowledged` };
  }
  if (po && (bill.kind === "balance" || bill.kind === "full") && po.expected_ship)
    return { date: String(po.expected_ship).slice(0, 10), why: `Due before ${po.number} ships` };
  return { date: null, why: "No due date" };
}

export type BillLineDraft = {
  kind: BillLineKind;
  description: string;
  po_id: string;
  po_line_id: string | null;
  credit_bill_id: string | null;
  item_id: string | null;
  qty: number | null;
  unit_price: number | null;
  amount: number;
};

/** "GCU2-SPRING GCU2 Trigger Spring + 2 more": what a bill's goods are, in a few words. */
const whatsOn = (lines: { description: string }[]) => (lines.length ? `${lines[0].description}${lines.length > 1 ? ` + ${lines.length - 1} more` : ""}` : "");

/**
 * The lines of a bill for part of a PO: its deposit (deposit_pct of the total), the balance (every line not billed
 * yet, less deposits not yet taken off) or what's been received and not billed (less those deposits too). The
 * browser previews it and bill.create_from_po makes the same.
 */
export function billFromPo(
  ix: BillIndex,
  poId: string,
  part: PoBillPart,
  dayOf: (ts: string) => string,
): { kind: BillKind; amount: number; lines: BillLineDraft[]; due: string | null; description: string; problem: string | null } {
  const plan = poBillPlan(ix, poId, dayOf);
  const none = { kind: "other" as BillKind, amount: 0, lines: [], due: null, description: "" };
  if (!plan) return { ...none, problem: "No such PO" };
  const po = plan.po;
  if (po.status === "draft" || po.status === "cancelled")
    return { ...none, problem: `${po.number} is ${po.status === "draft" ? "still a draft" : "cancelled"}` };
  // A line without a price can't be billed (it would bill as $0 and count as billed): its price comes first. A
  // deposit is a share of every line, so any line without one stops it too.
  const noPrice = (pl: PoLineRow) => ({ ...none, problem: `${po.number} line ${pl.position} has no price: set it first` });
  if (part === "deposit") {
    const unpricedLine = (ix.poLinesByPo.get(poId) ?? []).find((l) => l.unit_price == null);
    if (unpricedLine) return noPrice(unpricedLine);
    if (plan.depositPct <= 0) return { ...none, problem: `${po.number} has no deposit (its deposit % is 0)` };
    if (plan.deposit.billed > 0)
      return { ...none, problem: `${po.number}'s deposit is billed already (${plan.deposit.bills.map((b) => b.number).join(", ")})` };
    if (plan.deposit.amount <= 0) return { ...none, problem: `${po.number}'s lines have no prices yet` };
    if (plan.unbilled < plan.deposit.amount) return { ...none, problem: `${po.number} is billed in full already: no deposit is left to bill` };
    return {
      kind: "deposit",
      amount: plan.deposit.amount,
      due: plan.deposit.due,
      description: `${plan.depositPct}% deposit on ${po.number}`,
      lines: [
        {
          kind: "deposit",
          description: `${plan.depositPct}% deposit on ${po.number} (${moneyText(plan.total, po.currency)})`,
          po_id: po.id,
          po_line_id: null,
          credit_bill_id: null,
          item_id: null,
          qty: null,
          unit_price: null,
          amount: plan.deposit.amount,
        },
      ],
      problem: null,
    };
  }
  const lines: BillLineDraft[] = [];
  for (const pl of ix.poLinesByPo.get(poId) ?? []) {
    const billedQty = add(...(ix.linesByPoLine.get(pl.id) ?? []).filter((l) => l.kind === "goods").map((l) => Number(l.qty ?? 0)));
    const upTo = part === "received" ? deliveredQty(pl, ix.outputByLine.get(pl.id)).qty : Number(pl.qty);
    const qty = mulRound(upTo - billedQty, 1, 3);
    if (qty <= 0) continue;
    if (pl.unit_price == null) return noPrice(pl);
    const price = Number(pl.unit_price);
    lines.push({
      kind: "goods",
      description: `${pl.sku ? `${pl.sku} ` : ""}${pl.name}`.trim(),
      po_id: po.id,
      po_line_id: pl.id,
      credit_bill_id: null,
      item_id: pl.item_id,
      qty,
      unit_price: price,
      amount: mulRound(qty, price, 2),
    });
  }
  if (!lines.length)
    return {
      ...none,
      problem: part === "received" ? `Nothing received on ${po.number} is waiting to be billed` : `Every line of ${po.number} is billed already`,
    };
  // Deposits billed and not yet taken off another bill come off this one (never more than its goods).
  const goods = add(...lines.map((l) => l.amount));
  let room = goods;
  const credited = new Map<string, number>();
  for (const l of ix.linesByPo.get(poId) ?? [])
    if (l.kind === "deposit_credit" && l.credit_bill_id) credited.set(l.credit_bill_id, add(credited.get(l.credit_bill_id) ?? 0, -Number(l.amount)));
  for (const b of plan.deposit.bills) {
    const billed = add(...(ix.linesByBill.get(b.id) ?? []).filter((l) => l.kind === "deposit" && l.po_id === poId).map((l) => Number(l.amount)));
    const left = mulRound(billed - (credited.get(b.id) ?? 0), 1, 2);
    const take = Math.min(left, room);
    if (take <= 0) continue;
    room = mulRound(room - take, 1, 2);
    lines.push({
      kind: "deposit_credit",
      description: `Less deposit ${b.number}${b.invoice_number ? ` (${b.invoice_number})` : ""}`,
      po_id: po.id,
      po_line_id: null,
      credit_bill_id: b.id,
      item_id: null,
      qty: null,
      unit_price: null,
      amount: -take,
    });
  }
  const amount = add(...lines.map((l) => l.amount));
  const credits = lines.some((l) => l.kind === "deposit_credit");
  return {
    kind: credits || plan.deposit.billed > 0 ? "balance" : "full",
    amount,
    due: po.expected_ship ? String(po.expected_ship).slice(0, 10) : null,
    description: `${part === "received" ? "Received on " : credits ? "Balance of " : ""}${po.number}: ${whatsOn(lines.filter((l) => l.kind === "goods"))}`,
    lines,
    problem: null,
  };
}

// 3-way match -----------------------------------------------------------------------------------------------------

export type DiffKind =
  | "price"
  | "over_billed"
  | "billed_not_received"
  | "received_not_billed"
  | "deposit_amount"
  | "deposit_not_credited"
  | "over_credited"
  | "charge"
  | "lines_vs_total"
  | "cost_amount"
  | "rounding";
export const DIFF_LABELS: Record<DiffKind, string> = {
  rounding: "Rounding over $1",
  price: "Price",
  over_billed: "Billed more than ordered",
  billed_not_received: "Billed, not received",
  received_not_billed: "Received, not billed",
  deposit_amount: "Deposit amount",
  deposit_not_credited: "Deposit not taken off",
  over_credited: "Deposit taken off twice",
  charge: "Not on the PO",
  lines_vs_total: "Lines don't add up",
  cost_amount: "Amount vs the cost",
};
export type MatchDiff = {
  kind: DiffKind;
  poId: string | null;
  poLineId: string | null;
  billId: string | null;
  /** Its effect on what's paid, in the bill's currency: + is more than the PO / receipts say. */
  amount: number;
  text: string;
  /** Goods still on their way (billed ahead of receipt, as a balance before shipment is): not a difference yet. */
  waiting?: boolean;
};
export type MatchLine = {
  poLine: PoLineRow;
  ordered: number;
  price: number | null;
  /** Delivered: received at the ship-to, plus (Pre-sales) the units that went straight to backers once it's done. */
  received: number;
  /** Pre-sales --- of `received`, sent straight from the CM to backers. */
  toBackers: number;
  rejected: number;
  billed: number;
  billedAmount: number;
  bills: { billId: string; number: string; qty: number; price: number | null; amount: number }[];
};
export type MatchStatus = "match" | "waiting" | "differences" | "accepted" | "nothing" | "not_billed";
export const MATCH_LABELS: Record<MatchStatus, string> = {
  match: "Match",
  waiting: "Waiting for goods",
  differences: "Differences",
  accepted: "Accepted",
  nothing: "Nothing to match",
  not_billed: "Not billed yet",
};
export const MATCH_TONE = { match: "ok", waiting: "accent", differences: "warn", accepted: "ok", nothing: "neutral", not_billed: "neutral" } as const;
export type PoMatch = {
  po: PoRow;
  lines: MatchLine[];
  diffs: MatchDiff[];
  totals: { ordered: number; received: number; billedGoods: number; deposits: number; credits: number; charges: number; net: number };
  status: MatchStatus;
};

const round4 = (n: number) => Math.round(n * 10_000) / 10_000;
const goodsDone = (po: PoRow) => po.status === "received" || po.status === "closed";

/** PO lines × what was received × what's billed, with every difference in words. */
export function poMatch(ix: BillIndex, poId: string): PoMatch | null {
  const hit = ix.cache.po.get(poId);
  if (hit) return hit;
  const po = ix.poById.get(poId);
  if (!po) return null;
  const cur = po.currency;
  const diffs: MatchDiff[] = [];
  const lines: MatchLine[] = [];
  for (const pl of ix.poLinesByPo.get(poId) ?? []) {
    const bl = (ix.linesByPoLine.get(pl.id) ?? []).filter((l) => l.kind === "goods");
    const price = pl.unit_price == null ? null : Number(pl.unit_price);
    const delivered = deliveredQty(pl, ix.outputByLine.get(pl.id));
    const ml: MatchLine = {
      poLine: pl,
      ordered: Number(pl.qty),
      price,
      received: delivered.qty,
      toBackers: delivered.direct,
      rejected: Number(pl.qty_rejected),
      billed: mulRound(add(...bl.map((l) => Number(l.qty ?? 0))), 1, 3),
      billedAmount: add(...bl.map((l) => Number(l.amount))),
      bills: bl.map((l) => ({
        billId: l.bill_id,
        number: ix.billById.get(l.bill_id)?.number ?? "",
        qty: Number(l.qty ?? 0),
        price: l.unit_price == null ? null : Number(l.unit_price),
        amount: Number(l.amount),
      })),
    };
    lines.push(ml);
    const label = pl.sku || pl.name;
    for (const b of ml.bills)
      if (b.price != null && price != null && round4(b.price) !== round4(price)) {
        const effect = mulRound(b.qty, b.price - price, 2);
        diffs.push({
          kind: "price",
          poId,
          poLineId: pl.id,
          billId: b.billId,
          amount: effect,
          text: `${label}: ${b.number} bills ${moneyText(b.price, cur)} a unit, ${po.number} says ${moneyText(price, cur)} (${effect >= 0 ? "+" : ""}${moneyText(effect, cur)} on ${qtyText(b.qty)})`,
        });
      }
    const unit = price ?? 0;
    if (ml.billed > ml.ordered)
      diffs.push({
        kind: "over_billed",
        poId,
        poLineId: pl.id,
        billId: ml.bills.at(-1)?.billId ?? null,
        amount: mulRound(ml.billed - ml.ordered, unit, 2),
        text: `${label}: billed ${qtyText(ml.billed)}, ordered ${qtyText(ml.ordered)} (+${qtyText(ml.billed - ml.ordered)})`,
      });
    if (ml.billed > ml.received) {
      const short = mulRound(Math.min(ml.billed, ml.ordered) - ml.received, 1, 3);
      if (short > 0) {
        const waiting = !goodsDone(po);
        diffs.push({
          kind: "billed_not_received",
          poId,
          poLineId: pl.id,
          billId: ml.bills.at(-1)?.billId ?? null,
          amount: mulRound(short, unit, 2),
          waiting,
          text: waiting
            ? `${label}: ${qtyText(ml.billed)} billed, ${qtyText(ml.received)} received so far (${qtyText(short)} still to come${Number(pl.qty_direct ?? 0) > 0 && !ml.toBackers ? `; ${qtyText(Number(pl.qty_direct))} of them go straight to backers` : ""})`
            : `${label}: ${qtyText(ml.billed)} billed but ${qtyText(ml.received)} arrived${ml.toBackers ? ` (${qtyText(ml.toBackers)} of them straight to backers)` : ""} and nothing else is coming (${qtyText(short)} × ${moneyText(unit, cur)})`,
        });
      }
    }
    if (ml.received > ml.billed)
      diffs.push({
        kind: "received_not_billed",
        poId,
        poLineId: pl.id,
        billId: null,
        amount: -mulRound(ml.received - ml.billed, unit, 2),
        text: `${label}: ${qtyText(ml.received - ml.billed)} received, not billed yet (${moneyText(mulRound(ml.received - ml.billed, unit, 2), cur)})`,
      });
  }
  const all = ix.linesByPo.get(poId) ?? [];
  const deposits = add(...all.filter((l) => l.kind === "deposit").map((l) => Number(l.amount)));
  const credits = -add(...all.filter((l) => l.kind === "deposit_credit").map((l) => Number(l.amount)));
  const charges = add(...all.filter((l) => (l.kind === "charge" || l.kind === "other") && !l.po_line_id).map((l) => Number(l.amount)));
  const billedGoods = add(...lines.map((l) => l.billedAmount));
  const { total } = poTotal(ix, poId);
  const pct = Number(po.deposit_pct) || 0;
  if (deposits > 0 && pct > 0) {
    const expected = mulRound(total, pct / 100, 2);
    if (!same(deposits, expected)) {
      const depBill = all.find((l) => l.kind === "deposit")?.bill_id ?? null;
      diffs.push({
        kind: "deposit_amount",
        poId,
        poLineId: null,
        billId: depBill,
        amount: mulRound(deposits - expected, 1, 2),
        text: `Deposit billed ${moneyText(deposits, cur)}; ${pct}% of ${po.number} (${moneyText(total, cur)}) is ${moneyText(expected, cur)}`,
      });
    }
  }
  if (billedGoods > 0 && cents(credits) < cents(deposits)) {
    const last = all.filter((l) => l.kind === "goods").at(-1)?.bill_id ?? null;
    diffs.push({
      kind: "deposit_not_credited",
      poId,
      poLineId: null,
      billId: last,
      amount: mulRound(deposits - credits, 1, 2),
      text: `${moneyText(deposits - credits, cur)} of deposit isn't taken off the goods bills: paying them all pays it twice`,
    });
  }
  if (cents(credits) > cents(deposits))
    diffs.push({
      kind: "over_credited",
      poId,
      poLineId: null,
      billId: all.filter((l) => l.kind === "deposit_credit").at(-1)?.bill_id ?? null,
      amount: -mulRound(credits - deposits, 1, 2),
      text: `${moneyText(credits, cur)} of deposit taken off, but only ${moneyText(deposits, cur)} was billed`,
    });
  for (const l of all.filter((x) => (x.kind === "charge" || x.kind === "other") && !x.po_line_id && !x.production_cost_id && !x.tooling_item_id))
    diffs.push({
      kind: "charge",
      poId,
      poLineId: null,
      billId: l.bill_id,
      amount: Number(l.amount),
      text: `${ix.billById.get(l.bill_id)?.number ?? "A bill"} adds ${moneyText(Number(l.amount), cur)} that isn't on ${po.number}${l.description ? `: ${l.description}` : ""}`,
    });
  const receivedValue = add(...lines.map((l) => (l.price == null ? 0 : mulRound(l.received, l.price, 2))));
  const billedAny = all.length > 0;
  const real = diffs.filter((d) => !d.waiting && d.kind !== "received_not_billed");
  const status: MatchStatus = !billedAny
    ? "not_billed"
    : real.length
      ? "differences"
      : diffs.some((d) => d.waiting) || diffs.some((d) => d.kind === "received_not_billed")
        ? "waiting"
        : "match";
  const out: PoMatch = {
    po,
    lines,
    diffs,
    totals: { ordered: total, received: receivedValue, billedGoods, deposits, credits, charges, net: add(billedGoods, deposits, -credits, charges) },
    status,
  };
  ix.cache.po.set(poId, out);
  return out;
}

export type BillMatch = {
  status: MatchStatus;
  diffs: MatchDiff[];
  /** Goods still coming (billed ahead of receipt): shown, not counted as differences. */
  waiting: MatchDiff[];
  key: string;
  linesTotal: number;
  text: string;
};

/** FNV-1a, 32 bits, as hex: a short stable key for a set of differences (the browser has no crypto.hash). */
function fnv(s: string) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}
/** The differences as a key: accepting them stores it, and a new or changed difference shows again. */
export const matchKey = (diffs: MatchDiff[]) =>
  diffs.length
    ? fnv(
        diffs
          .map((d) => `${d.kind}:${d.poLineId ?? d.poId ?? ""}:${cents(d.amount)}`)
          .sort()
          .join("|"),
      )
    : "";

/** A bill against its POs, receipts, costs and tooling: Match, Waiting for goods, Differences or Accepted. */
export function billMatch(ix: BillIndex, billId: string): BillMatch {
  const hit = ix.cache.bill.get(billId);
  if (hit) return hit;
  const bill = ix.billById.get(billId) as Bill;
  const lines = ix.linesByBill.get(billId) ?? [];
  const cur = bill.currency;
  const diffs: MatchDiff[] = [];
  const waiting: MatchDiff[] = [];
  const linesTotal = add(...lines.map((l) => Number(l.amount)));
  // A bill entered with no lines that a vendor's rounding was put on still has no lines of its own to add up.
  if (lines.some((l) => l.kind !== "rounding") && !same(linesTotal, Number(bill.amount)))
    diffs.push({
      kind: "lines_vs_total",
      poId: null,
      poLineId: null,
      billId,
      amount: mulRound(Number(bill.amount) - linesTotal, 1, 2),
      text: `The lines add up to ${moneyText(linesTotal, cur)}; the bill says ${moneyText(Number(bill.amount), cur)}`,
    });
  const myPoLines = new Set(lines.map((l) => l.po_line_id).filter(Boolean));
  const hasGoods = lines.some((l) => l.kind === "goods");
  if (bill.status !== "void")
    for (const poId of new Set(lines.map((l) => l.po_id).filter((x): x is string => !!x))) {
      const m = poMatch(ix, poId);
      if (!m) continue;
      for (const d of m.diffs) {
        const mine =
          (d.kind === "price" && d.billId === billId) ||
          ((d.kind === "over_billed" || d.kind === "billed_not_received") && d.poLineId && myPoLines.has(d.poLineId)) ||
          (d.kind === "deposit_amount" && d.billId === billId) ||
          ((d.kind === "deposit_not_credited" || d.kind === "over_credited") && (hasGoods || d.billId === billId)) ||
          (d.kind === "charge" && d.billId === billId);
        if (!mine) continue;
        (d.waiting ? waiting : diffs).push(d);
      }
    }
  // A line for nothing in particular on a bill for a PO (a bank charge the vendor added): not on the PO either.
  const poLinked = lines.some((l) => l.po_id);
  // The vendor rounding its invoice (up to $1.00 on the bill, all its rounding lines together) is accounted for; more
  // than that is a difference to look at.
  const rounding = add(...lines.filter((l) => l.kind === "rounding").map((l) => Number(l.amount)));
  if (cents(Math.abs(rounding)) > cents(ROUNDING_MAX))
    diffs.push({
      kind: "rounding",
      poId: null,
      poLineId: null,
      billId,
      amount: rounding,
      text: `${bill.number} rounds by ${moneyText(rounding, cur)}: more than the ${moneyText(ROUNDING_MAX, cur)} a vendor's rounding can be`,
    });
  for (const l of lines) {
    if (poLinked && !l.po_id && !l.production_cost_id && !l.tooling_item_id && (l.kind === "charge" || l.kind === "other") && Number(l.amount) !== 0)
      diffs.push({
        kind: "charge",
        poId: null,
        poLineId: null,
        billId,
        amount: Number(l.amount),
        text: `${bill.number} adds ${moneyText(Number(l.amount), cur)} that isn't on its PO${l.description ? `: ${l.description}` : ""}`,
      });
    if (l.production_cost_id) {
      const c = ix.costById.get(l.production_cost_id);
      if (c && c.basis === "one_time" && c.currency.toUpperCase() === cur.toUpperCase() && !same(Number(c.amount), Number(l.amount)))
        diffs.push({
          kind: "cost_amount",
          poId: null,
          poLineId: null,
          billId,
          amount: mulRound(Number(l.amount) - Number(c.amount), 1, 2),
          text: `${moneyText(Number(l.amount), cur)} billed for "${c.description || c.invoice_number || "the run's cost"}", entered on the run as ${moneyText(Number(c.amount), c.currency)}`,
        });
    }
    if (l.tooling_item_id) {
      const t = ix.toolingById.get(l.tooling_item_id);
      if (t && t.currency.toUpperCase() === cur.toUpperCase() && !same(Number(t.amount), Number(l.amount)))
        diffs.push({
          kind: "cost_amount",
          poId: null,
          poLineId: null,
          billId,
          amount: mulRound(Number(l.amount) - Number(t.amount), 1, 2),
          text: `${moneyText(Number(l.amount), cur)} billed for ${t.name}, in the tooling ledger at ${moneyText(Number(t.amount), t.currency)}`,
        });
    }
  }
  const key = matchKey(diffs);
  const linked = lines.some((l) => l.po_id || l.production_cost_id || l.tooling_item_id);
  const status: MatchStatus = diffs.length
    ? bill.match_key && bill.match_key === key
      ? "accepted"
      : "differences"
    : !linked
      ? "nothing"
      : waiting.length
        ? "waiting"
        : "match";
  const text =
    status === "accepted"
      ? `Accepted: ${bill.match_note || "differences accepted"}`
      : status === "differences"
        ? `${diffs.length} ${diffs.length === 1 ? "difference" : "differences"}`
        : status === "waiting"
          ? "Billed ahead of the goods: matches so far"
          : status === "nothing"
            ? "Not linked to a PO or a run's cost"
            : "PO, receipts and bill agree";
  const out = { status, diffs, waiting, key, linesTotal, text };
  ix.cache.bill.set(billId, out);
  return out;
}

// Payments due ----------------------------------------------------------------------------------------------------

export const DUE_SECTIONS = ["overdue", "this_week", "deposits", "scheduled", "later", "drafts"] as const;
export type DueSection = (typeof DUE_SECTIONS)[number];
export const DUE_SECTION_LABELS: Record<DueSection, string> = {
  overdue: "Overdue",
  this_week: "Due this week",
  deposits: "Deposits waiting",
  scheduled: "Scheduled in Wise",
  later: "Later",
  drafts: "To approve",
};
export const DUE_SECTION_HINTS: Record<DueSection, string> = {
  overdue: "Approved bills past their due date",
  this_week: "Approved bills due in the next 7 days",
  deposits: "Deposits on sent POs: billed and unpaid, or not billed yet",
  scheduled: "Wise transfers made here, waiting for someone to approve and fund them in Wise (or being sent)",
  later: "Approved bills due after this week, or with no due date",
  drafts: "Bills entered but not approved yet: they aren't due until someone checks them",
};

export type DueRow = {
  key: string;
  section: DueSection;
  bill: Bill | null;
  /** A PO whose deposit isn't billed yet (no bill). */
  po: PoRow | null;
  vendorId: string;
  runIds: string[];
  /** Still to pay (for scheduled bills: what's on its way). */
  amount: number;
  currency: string;
  due: string | null;
  dueWhy: string;
  overdueDays: number;
  label: string;
  payments: Payment[];
};

export type DueBoard = {
  sections: Record<DueSection, DueRow[]>;
  totals: Record<DueSection, { usd: number; other: Record<string, number>; count: number }>;
  recentlyPaid: { payment: Payment; bills: Bill[] }[];
  recentTotal: { usd: number; other: Record<string, number> };
};

const DEPOSIT_PO_STATUSES = new Set(["sent", "acknowledged", "in_production", "shipped"]);
const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

/**
 * The Payments due board: every open bill in one section (scheduled, to approve, deposits, overdue, this week, later)
 * plus POs whose deposit isn't billed yet, and payments made in the last 30 days. Filters: vendor, run.
 */
export function paymentsDue(ix: BillIndex, opts: { dayOf: (ts: string) => string; vendorId?: string | null; runId?: string | null }): DueBoard {
  const today = ix.today;
  const sections = Object.fromEntries(DUE_SECTIONS.map((s) => [s, [] as DueRow[]])) as Record<DueSection, DueRow[]>;
  const want = (vendorId: string, runIds: string[]) => (!opts.vendorId || vendorId === opts.vendorId) && (!opts.runId || runIds.includes(opts.runId));
  for (const b of ix.d.bills) {
    if (b.status === "paid" || b.status === "void") continue;
    const st = billState(ix, b.id);
    const { runIds } = billLinks(ix, b.id);
    if (!want(b.vendor_id, runIds)) continue;
    const due = billDue(ix, b, opts.dayOf);
    const open = st.payments.filter((p) => OPEN_PAYMENT.has(p.payment.status)).map((p) => p.payment);
    const section: DueSection =
      b.status === "scheduled"
        ? "scheduled"
        : b.status === "draft"
          ? "drafts"
          : b.kind === "deposit"
            ? "deposits"
            : due.date && due.date < today
              ? "overdue"
              : due.date && due.date <= addDays(today, 6)
                ? "this_week"
                : "later";
    sections[section].push({
      key: `bill:${b.id}`,
      section,
      bill: b,
      po: null,
      vendorId: b.vendor_id,
      runIds,
      amount: section === "scheduled" ? st.pending || st.due : b.status === "draft" ? st.amount : st.due,
      currency: b.currency,
      due: due.date,
      dueWhy: due.why,
      overdueDays: due.date && due.date < today ? daysBetween(due.date, today) : 0,
      label: `${b.number}${b.invoice_number ? ` · ${b.invoice_number}` : ""}`,
      payments: open,
    });
  }
  // Deposits not billed yet: sent POs with a deposit % and no deposit bill.
  for (const po of ix.d.pos) {
    if (!DEPOSIT_PO_STATUSES.has(po.status) || !(Number(po.deposit_pct) > 0)) continue;
    const runIds = po.production_run_id ? [po.production_run_id] : [];
    if (!want(po.vendor_id, runIds)) continue;
    const plan = poBillPlan(ix, po.id, opts.dayOf);
    if (plan?.deposit.state !== "not_billed" || plan.deposit.amount <= 0) continue;
    // A PO billed in full already (no deposit bill, but nothing left to bill) needs no deposit.
    if (plan.unbilled <= 0) continue;
    sections.deposits.push({
      key: `po:${po.id}`,
      section: "deposits",
      bill: null,
      po,
      vendorId: po.vendor_id,
      runIds,
      amount: plan.deposit.amount,
      currency: po.currency,
      due: plan.deposit.due,
      dueWhy: plan.deposit.dueText,
      overdueDays: plan.deposit.due && plan.deposit.due < today ? daysBetween(plan.deposit.due, today) : 0,
      label: `${po.number}: ${plan.depositPct}% deposit, not billed yet`,
      payments: [],
    });
  }
  const byDue = (a: DueRow, b: DueRow) => (a.due ?? "9999").localeCompare(b.due ?? "9999") || a.label.localeCompare(b.label);
  for (const s of DUE_SECTIONS) sections[s].sort(byDue);
  const sum = (rows: { amount: number; currency: string }[]) => {
    const other: Record<string, number> = {};
    let usd = 0;
    for (const r of rows)
      if (r.currency.toUpperCase() === "USD") usd = add(usd, r.amount);
      else other[r.currency] = add(other[r.currency] ?? 0, r.amount);
    return { usd, other };
  };
  const totals = Object.fromEntries(DUE_SECTIONS.map((s) => [s, { ...sum(sections[s]), count: sections[s].length }])) as DueBoard["totals"];
  const since = addDays(today, -30);
  const recentlyPaid = ix.d.payments
    .filter((p) => p.status === "paid" && p.paid_on && String(p.paid_on).slice(0, 10) >= since)
    .map((p) => ({
      payment: p,
      bills: (ix.allocsByPayment.get(p.id) ?? []).map((a) => ix.billById.get(a.bill_id)).filter((b): b is Bill => !!b),
    }))
    .filter((r) => want(r.payment.vendor_id ?? "", [...new Set(r.bills.flatMap((b) => billLinks(ix, b.id).runIds))]))
    .sort((a, b) => String(b.payment.paid_on).localeCompare(String(a.payment.paid_on)));
  return { sections, totals, recentlyPaid, recentTotal: sum(recentlyPaid.map((r) => ({ amount: Number(r.payment.amount), currency: r.payment.currency }))) };
}

// Wise webhooks -------------------------------------------------------------------------------------------------

/**
 * The two webhooks HHL Ops uses, as `wise-subscribe` makes them and as Settings → Wise says to make them by hand in
 * Wise (one per event). `label` is the event's name in Wise's documentation. Any schema version works
 * (server/src/wise/events.ts reads them all); the newest offered, 4.0.0, times events to the millisecond.
 */
export const WISE_WEBHOOKS = [
  {
    trigger: "transfers#state-change",
    name: "HHL Ops transfer updates",
    label: "Transfer state change",
    does: "Moves payments along (waiting for funding, processing, sent) and marks bills paid, with Wise's receipt.",
  },
  {
    trigger: "transfers#active-cases",
    name: "HHL Ops transfer problems",
    label: "Transfer active cases",
    does: "Shows on a payment when Wise has a problem with it (for example it needs more information).",
  },
] as const;
export const WISE_WEBHOOK_VERSION = "4.0.0";
export const WISE_WEBHOOK_PATH = "/webhooks/wise";

// The Wise reference ----------------------------------------------------------------------------------------------

/** Wise allows up to 35 characters on USD transfers to banks abroad (SWIFT); shorter routes take what fits. */
export const WISE_REFERENCE_MAX = 35;

/**
 * What the vendor sees on their statement: "<vendor short> <PO> <bill>" (the bill's own invoice number, else ours),
 * e.g. "Tailhoo PO-1042 TH20260511066". Letters, digits, spaces and - . / only; the vendor name goes first, then the
 * end of the bill number, if it's too long.
 */
export function wiseReference(o: { vendorShort: string; poNumbers: string[]; billRef: string }, max = WISE_REFERENCE_MAX): string {
  const clean = (s: string) =>
    s
      .replace(/[^A-Za-z0-9 ./-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const pos = o.poNumbers.map(clean).filter(Boolean);
  const bill = clean(o.billRef);
  const vendor = clean(o.vendorShort);
  const tail = pos.includes(bill) ? [] : [bill];
  // Shorter and shorter until it fits: all of it, without the vendor, with only the first PO, then cut.
  for (const parts of [
    [vendor, ...pos, ...tail],
    [...pos, ...tail],
    [pos[0], ...tail],
  ]) {
    const ref = parts.filter(Boolean).join(" ");
    if (ref.length <= max) return ref;
  }
  return [pos[0], ...tail].filter(Boolean).join(" ").slice(0, max).trim();
}

/** The reference for paying one bill. */
export function referenceForBill(ix: BillIndex, billId: string, max = WISE_REFERENCE_MAX) {
  const bill = ix.billById.get(billId) as Bill;
  const pos = billLinks(ix, billId)
    .poIds.map((id) => ix.poById.get(id)?.number)
    .filter((n): n is string => !!n);
  return wiseReference({ vendorShort: vendorShortOf(ix.vendorById.get(bill.vendor_id)), poNumbers: pos, billRef: bill.invoice_number || bill.number }, max);
}

// Matching Wise history -------------------------------------------------------------------------------------------

const STOP = new Set(["hhl", "po", "pi", "inv", "invoice", "payment", "pay", "deposit", "balance", "for", "and", "the", "ref", "order", "no"]);
/** A reference reduced for comparing: no case, spaces or . _ / - */
export const refKey = (s: string) => s.toLowerCase().replace(/[\s._/-]+/g, "");

/**
 * The tokens of a payment reference that can name a document: "TH20260827066-067 HHL" → TH20260827066,
 * TH20260827067 (a range written short); "PO-PI-1013 PINES-INV-000606S" → PO-PI-1013, PINES-INV-000606S. Words like
 * HHL, PO or INV on their own don't count, nor do short tokens without a digit.
 */
export function refTokens(reference: string): string[] {
  const out = new Set<string>();
  for (const raw of reference.split(/[\s,;+&]+/)) {
    const t = raw.replace(/^[#:(]+|[):.]+$/g, "");
    if (!t || STOP.has(t.toLowerCase())) continue;
    const range = t.match(/^(.*?)(\d+)-(\d{1,4})$/);
    if (range && range[3].length < range[2].length) {
      const [, head, from, to] = range;
      const start = Number(from);
      const end = Number(from.slice(0, from.length - to.length) + to);
      if (end > start && end - start <= 20) {
        for (let n = start; n <= end; n++) out.add(`${head}${String(n).padStart(from.length, "0")}`);
        continue;
      }
    }
    if (!/\d/.test(t) && t.length < 6) continue;
    if (refKey(t).length < 4) continue;
    out.add(t);
  }
  return [...out];
}

/** A vendor name reduced like the SQL vendor_key(): no case, punctuation or trailing Co./Ltd./Inc. */
export const nameKey = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .replace(/( (co|company|corp|corporation|inc|incorporated|llc|ltd|limited|gmbh|plc|pte|pty|bv|srl))+$/, "")
    .replace(/\s+/g, "");
function namesMatch(payee: string, v: Pick<Vendor, "name" | "short_name" | "aliases">) {
  const p = nameKey(payee);
  if (!p) return false;
  for (const n of [v.name, v.short_name, ...((v.aliases as string[] | null) ?? [])]) {
    const k = nameKey(n ?? "");
    if (!k) continue;
    if (k === p) return true;
    const [a, b] = k.length < p.length ? [k, p] : [p, k];
    if (a.length >= 5 && b.includes(a)) return true;
  }
  return false;
}

export const MATCH_TARGET_KINDS = ["bill", "po", "po_unpriced", "production_cost", "tooling_item"] as const;
/** `po_unpriced`: a PO whose one line has no price yet; the payment sets it (what's left of the transfer ÷ qty). */
export type MatchTargetKind = (typeof MATCH_TARGET_KINDS)[number];
export type MatchTarget = {
  kind: MatchTargetKind;
  id: string;
  /** For a PO: which part this would pay. */
  part?: PoBillPart;
  amount: number;
  label: string;
};

/** Vendors round their invoices: a transfer this close to the documents (either way) pays them in full. */
export const ROUNDING_MAX = 1;
export const ADJUSTMENT_KINDS = ["rounding", "bank_charge", "shipping", "other"] as const;

/** Accounted for --- why a Wise transfer isn't a vendor bill (migration 074). */
export const WISE_REASONS = ["before_hhl_ops", "own_account", "refund", "contractor", "not_business", "other"] as const;
export type WiseReason = (typeof WISE_REASONS)[number];
export const WISE_REASON_LABELS: Record<WiseReason, string> = {
  before_hhl_ops: "Paid an order from before HHL Ops",
  own_account: "Between our own accounts",
  refund: "A refund or a return",
  contractor: "A contractor or a person",
  not_business: "Not a business expense",
  other: "Something else",
};
export type AdjustmentKind = (typeof ADJUSTMENT_KINDS)[number];
export const ADJUSTMENT_LABELS: Record<AdjustmentKind, string> = {
  rounding: "Vendor rounded",
  bank_charge: "Bank charge",
  shipping: "Shipping",
  other: "Other",
};
/** The run cost an extra becomes when what it's paid with is on a production run. */
export const ADJUSTMENT_CATEGORY = { bank_charge: "payment_fee", shipping: "freight", other: "other" } as const;
/**
 * What a transfer paid beyond (or short of) its documents: the vendor rounding (+ more, − less; ROUNDING_MAX at most),
 * or HHL paying extra (a bank charge, shipping, other). An extra on a production run (`run_id`) becomes a run cost there.
 */
export type MatchAdjustment = { kind: AdjustmentKind; amount: number; description: string; run_id?: string | null };

export type Suggestion = {
  targets: MatchTarget[];
  /** The vendor's rounding, so Confirm links it in one click (older stored suggestions have none). */
  adjustments?: MatchAdjustment[];
  vendorId: string | null;
  label: string;
  confidence: "exact" | "strong" | "possible";
  score: number;
  why: string[];
  /** Not linkable as it is: what to do first ("Set PO-1016's prices first", or Choose… for the rest). */
  blocked?: string;
};
export type TransferLike = {
  /** Wise's transfer number: a run cost paid outside HHL Ops whose paid_ref names it was paid by this transfer. */
  wise_transfer_id?: number | null;
  reference: string;
  recipient_id: number | null;
  payee_name: string;
  amount: number;
  currency: string;
  transfer_date: string | null;
  note?: string;
};

/** A wise_transfer row as matching reads it. */
export const transferLike = (
  t: Pick<WiseTransfer, "reference" | "recipient_id" | "payee_name" | "amount" | "currency" | "transfer_date" | "note"> &
    Partial<Pick<WiseTransfer, "wise_transfer_id">>,
): TransferLike => ({
  wise_transfer_id: t.wise_transfer_id == null ? null : Number(t.wise_transfer_id),
  reference: t.reference,
  recipient_id: t.recipient_id == null ? null : Number(t.recipient_id),
  payee_name: t.payee_name,
  amount: Number(t.amount),
  currency: t.currency,
  transfer_date: t.transfer_date ? String(t.transfer_date).slice(0, 10) : null,
  note: t.note,
});

type Candidate = MatchTarget & {
  vendorId: string | null;
  keys: string[];
  date: string | null;
  /** The date on the document itself (a bill's date, an invoice date, a PO's dates on paper), never when it was recorded. */
  docDate: string | null;
  /** The invoice it carries (a run cost's, a tooling item's, a bill's; a PO's PI): a PO's charges share it. */
  invoice: string;
  /** The POs it's for (a PO part: itself; a bill: its POs; a run cost entered against one). */
  poIds: string[];
  /** The production run it's on (a PO's run, a cost's, the run paying a tooling item, a bill's main run). */
  runId: string | null;
  /** po_unpriced: its amount is whatever the transfer leaves; `amount` is its priced lines (usually 0). */
  flexible?: boolean;
  /** po_unpriced: the line the payment prices. */
  unpriced?: { lineId: string; qty: number; label: string };
  /** A bill that already has a payment can't take another line (a vendor's rounding, an extra). */
  lineOk: boolean;
  /** Its currency: a transfer pays bills and POs in its own currency only. */
  currency: string;
  /** A run cost or tooling item's value in USD (at its usd_rate): a USD transfer pays it at that, as bills do. */
  usd?: number;
  /** A run cost or tooling item recorded as paid outside HHL Ops (no bill): only the transfer that paid it can take it. */
  paid?: Paid;
  blocked?: string;
};

const LIVE_PAYMENT: ReadonlySet<PaymentStatus> = new Set(["draft", "awaiting_funding", "processing", "paid"]);

/** How a run cost or tooling item was paid outside HHL Ops (its paid_on / paid_method / paid_ref). */
type Paid = { on: string; method: string; ref: string };
const paidOf = (x: { paid_on: string | null; paid_method: string; paid_ref: string }): Paid | undefined =>
  x.paid_on ? { on: String(x.paid_on).slice(0, 10), method: x.paid_method ?? "", ref: x.paid_ref ?? "" } : undefined;
/** "12 May". */
const shortDay = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const PAID_BY: Record<string, string> = { card: "card", wire: "wire", other: "other means", wise: "Wise" };

/**
 * Whether something recorded as paid outside HHL Ops was paid by this transfer: its paid_ref names the transfer
 * (TRANSFER-<id> or the number: `names`), or it's marked paid by Wise on the transfer's day with no ref. Anything else
 * (a card, a wire, another transfer) isn't this transfer's to pay: `elsewhere` says how it was paid.
 */
export function paidElsewhere(
  p: Paid | undefined,
  t: Pick<TransferLike, "wise_transfer_id" | "transfer_date">,
): { names: boolean; byThis: boolean; elsewhere: string | null } {
  if (!p) return { names: false, byThis: false, elsewhere: null };
  const numbers: string[] = p.ref.match(/\d+/g) ?? [];
  if (t.wise_transfer_id && numbers.includes(String(t.wise_transfer_id))) return { names: true, byThis: true, elsewhere: null };
  if (p.method === "wise" && !p.ref.trim() && t.transfer_date && p.on === t.transfer_date) return { names: false, byThis: true, elsewhere: null };
  const how = p.method === "wise" ? (p.ref.trim() ? `Wise (${p.ref.trim()})` : "another Wise transfer") : (PAID_BY[p.method] ?? "") || "someone else";
  return { names: false, byThis: false, elsewhere: `Paid by ${how} on ${shortDay(p.on)}` };
}

/**
 * What a candidate is to this transfer: its amount in the transfer's currency (a bill or PO in its own currency only;
 * a run cost or tooling item in its own, or at its USD value for a USD transfer, as bills do), whether it was paid
 * by this transfer (`names`: its paid_ref names it; `byThis`: that, or marked paid by Wise that day with no ref),
 * and why this transfer can't pay it (another currency, or paid already some other way).
 */
function gate(c: Candidate, t: TransferLike): { names: boolean; byThis: boolean; unavailable: string | null; amount: number } {
  const same = c.currency.toUpperCase() === t.currency.toUpperCase();
  const asUsd = !same && c.usd != null && t.currency.toUpperCase() === "USD";
  if (!same && !asUsd)
    return {
      names: false,
      byThis: false,
      amount: c.amount,
      unavailable: `${c.label} is in ${c.currency.toUpperCase()}; this transfer is ${t.currency.toUpperCase()}`,
    };
  const p = paidElsewhere(c.paid, t);
  return { names: p.names, byThis: p.byThis, unavailable: p.elsewhere, amount: asUsd ? (c.usd as number) : c.amount };
}
/** A run cost's or tooling item's amount for a transfer (see gate()): null when the transfer can't pay its currency. */
export function amountFor(x: { amount: number; currency: string; usd_rate: number }, transferCurrency: string): number | null {
  if (x.currency.toUpperCase() === transferCurrency.toUpperCase()) return Number(x.amount);
  if (transferCurrency.toUpperCase() !== "USD") return null;
  return mulRound(Number(x.amount), Number(x.usd_rate), 2);
}

function candidates(ix: BillIndex, dayOf: (ts: string) => string): Candidate[] {
  if (ix.cache.cands) return ix.cache.cands;
  const out: Candidate[] = [];
  const linkedCosts = new Set(ix.linesByCost.keys());
  const linkedTooling = new Set(ix.linesByTooling.keys());
  for (const b of ix.d.bills) {
    if (b.status === "void" || b.status === "paid") continue;
    const st = billState(ix, b.id);
    if (st.due <= 0) continue;
    const links = billLinks(ix, b.id);
    const keys = [b.number, b.invoice_number];
    for (const id of links.poIds) {
      const po = ix.poById.get(id);
      if (po) keys.push(po.number, po.vendor_ref);
    }
    for (const id of links.costIds) {
      const c = ix.costById.get(id);
      if (c) keys.push(c.invoice_number, c.paid_ref, c.external_ref);
    }
    out.push({
      kind: "bill",
      id: b.id,
      amount: st.due,
      label: `${b.number}${b.invoice_number ? ` (${b.invoice_number})` : ""}`,
      vendorId: b.vendor_id,
      keys,
      date: b.bill_date ? String(b.bill_date).slice(0, 10) : dayOf(b.created_at),
      docDate: b.bill_date ? String(b.bill_date).slice(0, 10) : null,
      invoice: b.invoice_number,
      poIds: links.poIds,
      runId: links.runIds[0] ?? null,
      lineOk: (b.status === "draft" || b.status === "approved") && !st.payments.some((p) => LIVE_PAYMENT.has(p.payment.status)),
      currency: b.currency,
    });
  }
  for (const po of ix.d.pos) {
    if (po.status === "draft" || po.status === "cancelled") continue;
    const plan = poBillPlan(ix, po.id, dayOf);
    if (!plan) continue;
    const keys = [po.number, po.vendor_ref];
    const date = po.dated_on ? String(po.dated_on).slice(0, 10) : po.sent_on ? String(po.sent_on).slice(0, 10) : dayOf(po.created_at);
    const base = {
      kind: "po" as const,
      id: po.id,
      vendorId: po.vendor_id,
      keys,
      date,
      docDate: po.dated_on ? String(po.dated_on).slice(0, 10) : po.sent_on ? String(po.sent_on).slice(0, 10) : null,
      invoice: po.vendor_ref,
      poIds: [po.id],
      runId: po.production_run_id,
      lineOk: true,
      currency: po.currency,
    };
    const named = `${po.number}${po.vendor_ref ? ` (${po.vendor_ref})` : ""}`;
    // A PO whose price isn't in yet (a line without one): the payment says what it cost, if it's the only one.
    if (plan.unpriced > 0) {
      const open = (ix.poLinesByPo.get(po.id) ?? []).filter((l) => l.unit_price == null);
      const line = open[0];
      const blocked =
        open.length > 1
          ? `Set ${po.number}'s prices first`
          : plan.depositPct > 0
            ? // A deposit transfer would be taken for the whole PO's price.
              `${po.number} takes a ${plan.depositPct}% deposit: set its prices first`
            : plan.bills.length
              ? `Set ${po.number}'s price first: it's billed already`
              : !(Number(line.qty) > 0)
                ? `${po.number}'s line has no quantity`
                : undefined;
      out.push({
        ...base,
        kind: "po_unpriced",
        amount: plan.total,
        label: `${named}: no price yet`,
        flexible: true,
        unpriced: { lineId: line.id, qty: Number(line.qty), label: line.sku || line.name },
        ...(blocked ? { blocked } : {}),
      });
      // Never billed as a deposit or a balance while a line has no price (it would bill as $0): only this one.
      continue;
    }
    // A PO recorded with no lines yet (no order document: production's PO-1016): a transfer naming it says so.
    if (!(ix.poLinesByPo.get(po.id) ?? []).length && !plan.bills.length) {
      out.push({
        ...base,
        kind: "po_unpriced",
        amount: 0,
        label: `${named}: no lines yet`,
        flexible: true,
        blocked: `${po.number} has no lines yet: add what it bought first`,
      });
      continue;
    }
    if (plan.unbilled <= 0 || plan.total <= 0) continue;
    if (plan.depositPct > 0 && plan.deposit.state === "not_billed")
      out.push({ ...base, part: "deposit", amount: plan.deposit.amount, label: `${po.number} deposit (${plan.depositPct}%)` });
    out.push({ ...base, part: "balance", amount: plan.unbilled, label: plan.deposit.billed > 0 ? `${po.number} balance` : named });
  }
  // Run costs and tooling in their own currency (a transfer pays only what's in its currency).
  for (const c of ix.d.costs) {
    if (linkedCosts.has(c.id) || c.basis !== "one_time" || !(Number(c.amount) > 0)) continue;
    // A payment fee with no vendor is Wise's (or the card's) own fee, taken on top of a transfer: never what one paid.
    if (c.category === "payment_fee" && !c.vendor_id) continue;
    const run = ix.runById.get(c.run_id);
    out.push({
      kind: "production_cost",
      id: c.id,
      amount: Number(c.amount),
      label: `${run?.number ?? "Run"} cost: ${c.description || c.invoice_number || c.category}`,
      // A cost with no vendor entered against a PO is that PO's vendor's (its bill goes to them).
      vendorId: c.vendor_id ?? (c.po_id ? (ix.poById.get(c.po_id)?.vendor_id ?? null) : null),
      keys: [c.invoice_number, c.paid_ref, c.external_ref],
      date: c.invoice_date ? String(c.invoice_date).slice(0, 10) : c.paid_on ? String(c.paid_on).slice(0, 10) : null,
      docDate: c.invoice_date ? String(c.invoice_date).slice(0, 10) : null,
      invoice: c.invoice_number,
      poIds: c.po_id ? [c.po_id] : [],
      runId: c.run_id,
      lineOk: true,
      currency: c.currency,
      usd: amountFor(c, "USD") ?? undefined,
      paid: paidOf(c),
    });
  }
  for (const t of ix.d.tooling) {
    if (linkedTooling.has(t.id) || !(Number(t.amount) > 0)) continue;
    out.push({
      kind: "tooling_item",
      id: t.id,
      amount: Number(t.amount),
      label: `Tooling: ${t.name}`,
      vendorId: t.vendor_id,
      keys: [t.invoice_number, t.paid_ref],
      date: t.invoice_date ? String(t.invoice_date).slice(0, 10) : null,
      docDate: t.invoice_date ? String(t.invoice_date).slice(0, 10) : null,
      invoice: t.invoice_number,
      poIds: [],
      runId: t.paid_by_run_id,
      lineOk: true,
      currency: t.currency,
      usd: amountFor(t, "USD") ?? undefined,
      paid: paidOf(t),
    });
  }
  ix.cache.cands = out;
  return out;
}

const keyMatches = (token: string, key: string) => {
  const t = refKey(token);
  const k = refKey(key);
  if (k.length < 4 || t.length < 4) return false;
  if (t === k) return true;
  // Only digits, 8 or fewer ("20260511"): the whole key or a whole part of it, never inside one (TH20260511066), and
  // never one of HHL Ops' own PO or bill numbers by its digits alone ("1004" isn't PO-1004; "PO1004" is).
  if (/^\d{1,8}$/.test(t)) {
    if (/^(po|bl)-?\d+$/i.test(key.trim())) return false;
    // Four digits or fewer (a year, an old PO number): the whole key only, never a part of one ("GCU2-KS-2026-ASSY").
    if (t.length <= 4) return false;
    return key
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .includes(t);
  }
  // "PI SG20260518R01" vs a vendor ref "SG20260518R01-A": one inside the other, long and mostly digits.
  const [a, b] = t.length < k.length ? [t, k] : [k, t];
  return a.length >= 8 && (a.match(/\d/g)?.length ?? 0) >= 6 && b.includes(a);
};

/** "Vendor rounded: $0.50 less than the documents" (`diff` = the transfer − the documents). */
export const roundingText = (diff: number, cur = "USD") => `Vendor rounded: ${moneyText(Math.abs(diff), cur)} ${diff < 0 ? "less" : "more"} than the documents`;
const roundingAdjustment = (diff: number, cur: string): MatchAdjustment => ({ kind: "rounding", amount: diff, description: roundingText(diff, cur) });
/** "Shipping" → "shipping" in a sentence; "FCC certification" stays. */
const lowerFirst = (w: string) => (w.length > 1 && w[1] === w[1].toLowerCase() ? w[0].toLowerCase() + w.slice(1) : w);
/** A price to 4 places (what PO lines keep). */
const price4 = (amount: number, qty: number) => Math.round((amount * 10_000) / qty) / 10_000;
/** A unit price, to 4 places when it has them: "$0.887", "$1.4268". */
export const priceText = (n: number, cur = "USD") =>
  `${cur.toUpperCase() === "USD" ? "$" : ""}${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}${cur.toUpperCase() === "USD" ? "" : ` ${cur}`}`;
const toTarget = (c: Candidate, amount = c.amount): MatchTarget => ({ kind: c.kind, id: c.id, ...(c.part ? { part: c.part } : {}), amount, label: c.label });

/** The vendor a transfer went to: its Wise recipient's (linked), else the one vendor the payee's name is. */
export function transferVendor(ix: BillIndex, t: Pick<TransferLike, "recipient_id" | "payee_name">): { vendorId: string; how: "recipient" | "payee" } | null {
  const byRecipient = t.recipient_id != null ? ix.vendorByRecipient.get(Number(t.recipient_id)) : undefined;
  if (byRecipient) return { vendorId: byRecipient, how: "recipient" };
  if (!t.payee_name.trim()) return null;
  const hits = ix.d.vendors.filter((v) => namesMatch(t.payee_name, v));
  if (hits.length === 1) return { vendorId: hits[0].id, how: "payee" };
  const p = nameKey(t.payee_name);
  const exact = hits.filter((v) => [v.name, v.short_name, ...((v.aliases as string[] | null) ?? [])].some((n) => nameKey(n ?? "") === p));
  return exact.length === 1 ? { vendorId: exact[0].id, how: "payee" } : null;
}

/**
 * What a Wise transfer probably paid, best first: by reference (a token naming a bill, a PO or its PI, a run cost's
 * invoice; or a run cost recorded as paid by this very transfer), amount (what's left on a bill, a PO's deposit or
 * balance, a cost; to the cent, or within ROUNDING_MAX when the reference names it: the vendor rounded) and who it
 * went to (the vendor's linked Wise recipient, else the payee's name). "exact" = reference + amount to the cent +
 * vendor. Combinations: several documents the reference names whose amounts add up ("TH…066-067" pays two PIs), and
 * a PO part with the same vendor's run costs, tooling and bills on the same invoice (the reference names them, or
 * their invoice is the PO's number or PI): "PO-1008 + 3 charges on TH20260511066"; when several sets of charges fit,
 * up to 3 are offered and none is exact. A PO without its price yet is suggested alone, paid by the whole transfer
 * (never "exact"). Only things in the transfer's currency, and never a cost paid some other way. Confirm takes one
 * click only when the reference names what it pays, or the amount is exact and it went to the vendor's linked
 * recipient; anything else (near amounts, part payments, a transfer over 30 days before the document) is "possible"
 * and blocked: Choose… `ctx.namers` (unpricedNamers()): how many transfers name each PO without its price.
 */
export function suggestMatches(
  ix: BillIndex,
  t: TransferLike,
  dayOf: (ts: string) => string,
  ctx: { namers?: ReadonlyMap<string, number> } = {},
): Suggestion[] {
  const tokens = refTokens(`${t.reference}`);
  const cur = t.currency;
  const money = (n: number) => moneyText(n, cur);
  const recipientVendor = t.recipient_id != null ? (ix.vendorByRecipient.get(Number(t.recipient_id)) ?? null) : null;
  const scored: (Suggestion & { cand: Candidate[] })[] = [];
  // What this transfer can pay (its currency, nothing paid already some other way), at its amount for it.
  const gates = new Map<Candidate, ReturnType<typeof gate>>();
  const cands: Candidate[] = [];
  for (const c of candidates(ix, dayOf)) {
    const g = gate(c, t);
    if (g.unavailable) continue;
    const mine = g.amount === c.amount ? c : { ...c, amount: g.amount };
    gates.set(mine, g);
    cands.push(mine);
  }
  const vendorOk = (vendorId: string | null) => {
    if (!vendorId) return { ok: false, recipient: false, name: false, mismatch: false };
    const recipient = recipientVendor === vendorId;
    const v = ix.vendorById.get(vendorId);
    const name = !!v && !!t.payee_name && namesMatch(t.payee_name, v);
    const mismatch = !recipient && !name && (recipientVendor != null || (!!t.payee_name && !!v));
    return { ok: recipient || name, recipient, name, mismatch };
  };
  const vendorWhy = (v: ReturnType<typeof vendorOk>) =>
    v.recipient ? ["Sent to the vendor's linked Wise recipient"] : v.name ? [`Payee "${t.payee_name}" is the vendor`] : [];
  const tooEarly = (c: Candidate) => !!(c.date && t.transfer_date && c.date > addDays(t.transfer_date, 30));
  /**
   * A transfer over 30 days before the date on a document: never exact or one click for it. Only the document's own
   * date: a PO or bill recorded later from paper (paid before HHL Ops) isn't early by when it was recorded.
   */
  const early = (c: Candidate) => !!(c.docDate && t.transfer_date && c.docDate > addDays(t.transfer_date, 30));
  const EARLY = "Paid over 30 days before the document's date: use Choose… to check it";
  const hitsOf = new Map<Candidate, string[]>();
  const named = (c: Candidate) => {
    let h = hitsOf.get(c);
    if (!h) hitsOf.set(c, (h = tokens.filter((tok) => c.keys.some((k) => k && keyMatches(tok, k)))));
    return h;
  };
  /** A run cost or tooling item recorded as paid by this transfer (its paid_ref names it). */
  const namesIt = (c: Candidate) => !!gates.get(c)?.names;
  const byRef = (c: Candidate) => named(c).length > 0 || namesIt(c);
  /** A bill's rounding already on it plus this stays within ROUNDING_MAX (what planLink allows). */
  const roomFor = (c: Candidate, diff: number) => {
    const on = c.kind === "bill" ? add(...(ix.linesByBill.get(c.id) ?? []).filter((l) => l.kind === "rounding").map((l) => Number(l.amount))) : 0;
    return cents(Math.abs(add(on, diff))) <= cents(ROUNDING_MAX);
  };
  const tc = cents(t.amount);

  for (const c of cands) {
    const refHits = named(c);
    if (c.flexible) {
      // A PO without its price: only the reference can say this paid it (any amount would add up).
      if (!refHits.length || (!c.blocked && tc <= cents(c.amount))) continue;
      const v = vendorOk(c.vendorId);
      if (v.mismatch) continue;
      const u = c.unpriced as NonNullable<Candidate["unpriced"]>;
      const forLine = mulRound(t.amount - c.amount, 1, 2);
      const po = ix.poById.get(c.id) as PoRow;
      // Not this transfer's alone to price: a charge of the vendor's was paid by it too (split it in Choose…), or
      // another transfer names the PO too (a deposit and a balance?).
      const charge = cands.find((x) => (x.kind === "production_cost" || x.kind === "tooling_item") && x.vendorId === c.vendorId && gates.get(x)?.byThis);
      const namers = ctx.namers?.get(c.id) ?? 0;
      const blocked =
        c.blocked ??
        (charge
          ? `${po.number} has no price, and ${money(charge.amount)} ${lowerFirst(charge.label.split(": ").at(-1) ?? charge.label)}${charge.invoice ? ` on ${charge.invoice}` : ""} ${gates.get(charge)?.names ? "names this transfer" : "was paid by Wise that day"}: use Choose… to split it`
          : namers > 1
            ? `${namers} transfers name ${po.number} (a deposit and a balance?): set its prices first`
            : early(c)
              ? EARLY
              : undefined);
      scored.push({
        targets: [toTarget(c, t.amount)],
        adjustments: [],
        vendorId: c.vendorId,
        label: c.label,
        confidence: v.ok && !early(c) ? "strong" : "possible",
        score: 50 + (v.recipient ? 25 : v.name ? 15 : 0),
        why: [
          `The reference names it (${refHits.join(", ")})`,
          c.blocked
            ? `${c.blocked}: then its amount can be compared`
            : `No price yet: the payment sets it, ${money(forLine)} ÷ ${qtyText(u.qty)} = ${priceText(price4(forLine, u.qty), cur)} a unit`,
          ...vendorWhy(v),
        ],
        ...(blocked ? { blocked } : {}),
        cand: [c],
      });
      continue;
    }
    const ref = byRef(c);
    const diff = mulRound(t.amount - c.amount, 1, 2);
    const exactAmount = cents(diff) === 0;
    // A vendor's rounding only for something the reference names.
    const rounded = ref && !exactAmount && cents(Math.abs(diff)) <= cents(ROUNDING_MAX) && roomFor(c, diff);
    const closeAmount = !exactAmount && !rounded && c.amount > 0 && Math.abs(diff) / c.amount <= 0.01;
    // The vendor alone isn't a match: it takes the reference or the amount.
    if (!ref && !exactAmount && !closeAmount) continue;
    const v = vendorOk(c.vendorId);
    let score = 0;
    const why: string[] = [];
    if (refHits.length) {
      score += 50;
      why.push(`The reference names it (${refHits.join(", ")})`);
    }
    if (namesIt(c)) {
      score += refHits.length ? 10 : 50;
      why.push("This cost names the transfer");
    }
    if (exactAmount) {
      score += 30;
      why.push(`Same amount, ${money(t.amount)}`);
    } else if (rounded) {
      score += 25;
      why.push(roundingText(diff, cur));
    } else if (closeAmount) {
      score += 10;
      why.push(`Within 1% of ${moneyText(c.amount)}`);
    }
    if (v.recipient) score += 25;
    else if (v.name) score += 15;
    why.push(...vendorWhy(v));
    if (v.mismatch) {
      score -= 40;
      why.push("Sent to someone else");
    }
    if (tooEarly(c)) {
      score -= 20;
      why.push("Paid before the document's date");
    }
    if (score < 25) continue;
    let confidence: Suggestion["confidence"] = exactAmount
      ? ref && v.ok
        ? "exact"
        : v.recipient || (ref && !v.mismatch)
          ? "strong"
          : "possible"
      : ref && v.ok
        ? "strong"
        : "possible";
    // What Confirm would send: all of it; part of it (the transfer is less); or nothing until someone says what the
    // rest of a bigger transfer paid.
    let targets = [toTarget(c)];
    let adjustments: MatchAdjustment[] = [];
    let blocked: string | undefined;
    if (rounded) {
      adjustments = [roundingAdjustment(diff, cur)];
      if (!c.lineOk) blocked = `${c.label} has a payment already, so the rounding can't go on it: use Choose…`;
    } else if (!exactAmount && diff < 0) {
      targets = [toTarget(c, t.amount)];
      why.push(`Pays ${money(t.amount)} of ${money(c.amount)}: ${money(-diff)} left to pay`);
    } else if (!exactAmount) blocked = `The transfer is ${money(diff)} more than this: use Choose… to say what the rest paid`;
    if (early(c)) {
      confidence = "possible";
      blocked ??= EARLY;
    }
    // One click needs the reference to name it, or the exact amount to the vendor's linked recipient.
    if (!ref && !(exactAmount && v.recipient)) {
      confidence = "possible";
      blocked ??= exactAmount
        ? "Only the amount and the payee match, nothing in the reference: use Choose… to check it"
        : "Only near the amount, nothing in the reference: use Choose… to check it";
    }
    scored.push({
      targets,
      adjustments,
      vendorId: c.vendorId,
      label: c.label,
      confidence,
      score,
      why,
      ...(blocked ? { blocked } : {}),
      cand: [c],
    });
  }

  /** Where a vendor's rounding goes: the largest one that can take a line. */
  const roundsOn = (list: Candidate[]) => [...list].filter((c) => c.lineOk).sort((a, b) => b.amount - a.amount)[0] ?? null;
  /** Whether rounding it can carry this much more (planLink's cap: a bill's rounding all told, ROUNDING_MAX). */
  const canRound = (list: Candidate[], diff: number) => {
    const on = roundsOn(list);
    return !!on && roomFor(on, diff);
  };

  // Several documents the reference names, of one vendor, whose amounts add up to the transfer (or within rounding).
  const namedDocs = cands.filter((c) => byRef(c) && c.kind !== "po" && !c.flexible);
  for (const [vendorId, list] of group(namedDocs, (c) => c.vendorId ?? "")) {
    const uniq = [...new Map(list.map((c) => [`${c.kind}:${c.id}`, c])).values()];
    if (uniq.length < 2) continue;
    const total = add(...uniq.map((c) => c.amount));
    const diff = mulRound(t.amount - total, 1, 2);
    if (cents(Math.abs(diff)) > cents(ROUNDING_MAX)) continue;
    const v = vendorOk(vendorId || null);
    if (v.mismatch) continue;
    const exactSum = cents(diff) === 0;
    if (!exactSum && !canRound(uniq, diff)) continue;
    const isEarly = uniq.some(early);
    scored.push({
      targets: uniq.map((c) => toTarget(c)),
      adjustments: exactSum ? [] : [roundingAdjustment(diff, cur)],
      vendorId: vendorId || null,
      label: uniq.map((c) => c.label).join(" + "),
      confidence: isEarly ? "possible" : exactSum ? (v.ok ? "exact" : "strong") : v.ok ? "strong" : "possible",
      ...(isEarly ? { blocked: EARLY } : {}),
      score: exactSum ? 110 : 100,
      why: [
        exactSum
          ? `The reference names ${uniq.length} of them and they add up to ${money(t.amount)}`
          : `The reference names ${uniq.length} of them, together ${money(total)}`,
        ...(exactSum ? [] : [roundingText(diff, cur)]),
        ...(v.ok ? ["Sent to the vendor"] : []),
      ],
      cand: uniq,
    });
  }

  // A PO part with the charges on the same vendor invoice: run costs, tooling and bills the reference names (or that
  // name this transfer as paying them), or whose invoice is the PO's number or PI. Never the PO's own bills (they're
  // the PO), and never a PO without its price (that one's suggested alone: Choose… combines it, showing the price).
  const COMPANIONS = 12;
  const OFFER = 3;
  for (const p of cands) {
    if (p.kind !== "po" || p.blocked) continue;
    const poHits = named(p);
    if (!poHits.length) continue;
    const v = vendorOk(p.vendorId);
    if (v.mismatch) continue;
    const po = ix.poById.get(p.id) as PoRow;
    const poKeys = [po.number, po.vendor_ref].filter(Boolean);
    const companions = cands
      .filter(
        (c) =>
          (c.kind === "production_cost" || c.kind === "tooling_item" || c.kind === "bill") &&
          c.vendorId === p.vendorId &&
          !(c.kind === "bill" && c.poIds.includes(p.id)) &&
          (byRef(c) || (!!c.invoice && poKeys.some((k) => keyMatches(c.invoice, k)))),
      )
      .sort((a, b) => Number(byRef(b)) - Number(byRef(a)) || b.amount - a.amount)
      .slice(0, COMPANIONS);
    if (!companions.length) continue;
    // Every set of charges that, with the PO part, adds up to the transfer (to the cent, or within rounding): the
    // closest first, then the most charges.
    const cc = companions.map((c) => cents(c.amount));
    const fits: { mask: number; diff: number; n: number }[] = [];
    for (let mask = 1; mask < 1 << companions.length; mask++) {
      let sum = cents(p.amount);
      let n = 0;
      for (let i = 0; i < companions.length; i++)
        if (mask & (1 << i)) {
          sum += cc[i];
          n++;
        }
      const d = tc - sum;
      if (Math.abs(d) <= cents(ROUNDING_MAX)) fits.push({ mask, diff: d, n });
    }
    // A rounded one only where the rounding fits on the bill it goes on.
    const possible = fits.filter((f) => f.diff === 0 || canRound([p, ...companions.filter((_, i) => f.mask & (1 << i))], f.diff / 100));
    fits.length = 0;
    fits.push(...possible);
    if (!fits.length) continue;
    fits.sort((a, b) => Math.abs(a.diff) - Math.abs(b.diff) || b.n - a.n);
    // More than one way to make the amount: none is exact (nor linked by itself); the person checks which.
    const ways = fits.length > 1 ? `${fits.length} ways to make ${money(t.amount)}: check which charges it paid` : null;
    for (const fit of fits.slice(0, OFFER)) {
      const set = companions.filter((_, i) => fit.mask & (1 << i));
      const diff = fit.diff / 100;
      const exactSum = cents(diff) === 0;
      const all = [p, ...set];
      const invoices = [...new Set(set.map((c) => refKey(c.invoice)))];
      const on = invoices.length === 1 && invoices[0] ? ` on ${set[0].invoice}` : "";
      const refHits = [...new Set(all.flatMap((c) => named(c)))];
      const isEarly = all.some(early);
      scored.push({
        targets: all.map((c) => toTarget(c)),
        adjustments: exactSum ? [] : [roundingAdjustment(diff, cur)],
        vendorId: p.vendorId,
        label: `${po.number} + ${set.length} ${set.length === 1 ? "charge" : "charges"}${on}`,
        confidence: isEarly ? "possible" : exactSum && !ways ? (v.ok ? "exact" : "strong") : v.ok ? "strong" : "possible",
        ...(isEarly ? { blocked: EARLY } : {}),
        score: (exactSum ? 115 : 100) - (ways ? 10 : 0),
        why: [
          ...(ways ? [ways] : []),
          `The reference names ${refHits.join(", ")}`,
          ...all.map((c) => `${c.label}: ${money(c.amount)}${namesIt(c) ? " (it names this transfer)" : ""}`),
          exactSum ? `Together ${money(t.amount)}: the transfer's amount` : roundingText(diff, cur),
          ...vendorWhy(v),
        ],
        cand: all,
      });
    }
  }

  // A bill and the PO it's on are the same thing: keep the bill when both are suggested.
  const billPos = new Set(scored.flatMap((s) => s.cand.filter((c) => c.kind === "bill").flatMap((c) => billLinks(ix, c.id).poIds)));
  const rank = { exact: 3, strong: 2, possible: 1 };
  const seen = new Set<string>();
  return scored
    .filter((s) => !(s.targets.length === 1 && s.targets[0].kind === "po" && billPos.has(s.targets[0].id) && s.confidence !== "exact"))
    .sort((a, b) => rank[b.confidence] - rank[a.confidence] || Number(!!a.blocked) - Number(!!b.blocked) || b.score - a.score)
    .filter((s) => {
      const key = s.targets
        .map((x) => `${x.kind}:${x.id}:${x.part ?? ""}`)
        .sort()
        .join("|");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 6)
    .map(({ cand: _c, ...s }) => s);
}

/**
 * How many of these transfers (sent and not ignored: the caller's list) name each PO without its price. More than one
 * (a deposit and a balance?) and none of them may price it by itself: suggestMatches() blocks them (`ctx.namers`).
 */
export function unpricedNamers(ix: BillIndex, transfers: readonly Pick<TransferLike, "reference">[], dayOf: (ts: string) => string): Map<string, number> {
  const out = new Map<string, number>();
  const pos = candidates(ix, dayOf).filter((c) => c.kind === "po_unpriced");
  if (!pos.length) return out;
  for (const t of transfers) {
    const tokens = refTokens(t.reference);
    for (const c of pos) if (tokens.some((tok) => c.keys.some((k) => k && keyMatches(tok, k)))) out.set(c.id, (out.get(c.id) ?? 0) + 1);
  }
  return out;
}

/**
 * The one suggestion safe to link without asking: exactly one "exact", and nothing else strong beside it. Never one
 * with the vendor's rounding, a price the payment would set, or one that can't be linked as it is.
 */
export function autoLinkable(s: Suggestion[]): Suggestion | null {
  const plain = (x: Suggestion) => !x.blocked && !x.adjustments?.length && !x.targets.some((t) => t.kind === "po_unpriced");
  const exact = s.filter((x) => x.confidence === "exact" && plain(x));
  if (exact.length !== 1) return null;
  const rivals = s.filter((x) => x !== exact[0] && x.confidence !== "possible" && x.score >= exact[0].score - 20);
  return rivals.length ? null : exact[0];
}

// Choosing by hand -------------------------------------------------------------------------------------------------

/** Something of a vendor's a transfer can pay, for the Choose… dialog: `due` is the most it takes (null: any amount). */
export type Choice = MatchTarget & {
  due: number | null;
  /** po_unpriced: its priced lines; the amount must be more. */
  priced: number;
  runId: string | null;
  date: string | null;
  invoice: string;
  currency: string;
  /** Needs something first ("Set PO-1016's prices first"): not selectable. */
  blocked?: string;
  /** Not this transfer's to pay ("Paid by card on 12 May", another currency): shown, not selectable. */
  unavailable?: string;
  /** A cost recorded as paid by this transfer: its paid_ref names it. */
  namesTransfer?: boolean;
  /** A cost recorded as paid by this transfer (named, or by Wise that day): the dialog ticks it to start with. */
  paidByThis?: boolean;
};

/**
 * A vendor's open bills, PO parts (deposit, balance, no price yet), unlinked one-time run costs and tooling; with a
 * transfer, at their amount for it (a run cost at its USD value for a USD transfer), and what it can't pay marked
 * (`unavailable`: paid some other way, another currency).
 */
export function choosable(ix: BillIndex, vendorId: string, dayOf: (ts: string) => string, t?: TransferLike): Choice[] {
  const order: Record<MatchTargetKind, number> = { po: 0, po_unpriced: 0, bill: 1, production_cost: 2, tooling_item: 3 };
  return candidates(ix, dayOf)
    .filter((c) => c.vendorId === vendorId)
    .map((c) => {
      const g = t ? gate(c, t) : null;
      const amount = g && !g.unavailable ? g.amount : c.amount;
      return {
        ...toTarget(c, amount),
        due: c.flexible ? null : amount,
        priced: c.flexible ? amount : 0,
        runId: c.runId,
        date: c.date,
        invoice: c.invoice,
        currency: c.currency,
        ...(c.blocked ? { blocked: c.blocked } : {}),
        ...(g?.unavailable ? { unavailable: g.unavailable } : {}),
        ...(g?.names ? { namesTransfer: true } : {}),
        ...(g?.byThis ? { paidByThis: true } : {}),
      };
    })
    .sort((a, b) => order[a.kind] - order[b.kind] || String(b.date ?? "").localeCompare(String(a.date ?? "")) || a.label.localeCompare(b.label));
}

/** For a transfer with no match: its payee is a vendor with nothing open here it could pay (an order from before HHL Ops). */
export function noMatchHint(ix: BillIndex, t: TransferLike, dayOf: (ts: string) => string): string | null {
  const tv = transferVendor(ix, t);
  if (!tv || choosable(ix, tv.vendorId, dayOf, t).some((c) => !c.unavailable)) return null;
  return `No open PO, bill or run cost of ${vendorLabelOf(ix.vendorById.get(tv.vendorId))} here (paid before HHL Ops?)`;
}

/** What wise_transfer.link is asked to link (the suggestion's Confirm and the Choose… dialog send the same). */
export type LinkInput = {
  targets: { kind: MatchTargetKind; id: string; part?: PoBillPart; amount: number }[];
  adjustments?: MatchAdjustment[];
};
/** A line a target's bill gets: the vendor's rounding, an extra charge (`runId`: a run cost made for it), a price's cents. */
export type LinkLine = {
  kind: "rounding" | "charge";
  amount: number;
  description: string;
  /** The adjustment it is (null: a price written to 4 places, to the cent). */
  adjustment: number | null;
  /** An extra on a run: the run cost goes there, with this invoice number and PO. */
  runId: string | null;
  invoice: string;
  poId: string | null;
};
export type LinkPlan = {
  problems: string[];
  vendorId: string | null;
  total: number;
  /** Per target: what its bill gets beyond its own lines. */
  lines: LinkLine[][];
  /** Per target: what the payment pays its bill (its amount, plus its rounding and extras). */
  pays: number[];
  /** Per target, for a PO without a price: the price the payment sets on its line. */
  prices: ({ lineId: string; price: number; qty: number; forLine: number; label: string } | null)[];
  /** Paid in part: "PO-1009 keeps $40.00 to pay". */
  keeps: { label: string; left: number }[];
};

/**
 * Checks what a transfer is linked to, for the dialog (live) and the server (again, inside the writer): each target
 * open and paid no more than it owes (in part is fine), never twice, never a PO with its own bill, one vendor and the
 * one the transfer went to; the vendor's rounding ROUNDING_MAX at most, on the largest thing paid in full that can
 * take a line; an extra on a run that something picked is on, else on the largest one's bill; a PO without its price
 * gets it from what's left (to 4 places, the cents as rounding); and it all adds up to the transfer, to the cent.
 */
export function planLink(ix: BillIndex, t: TransferLike, input: LinkInput, dayOf: (ts: string) => string): LinkPlan {
  const cur = t.currency;
  const money = (n: number) => moneyText(n, cur);
  const problems: string[] = [];
  const adjustments = input.adjustments ?? [];
  const lines: LinkLine[][] = input.targets.map(() => []);
  const prices: LinkPlan["prices"] = input.targets.map(() => null);
  const keeps: LinkPlan["keeps"] = [];
  const info = input.targets.map((x, idx) => {
    const r = {
      label: "That",
      vendorId: null as string | null,
      due: null as number | null,
      lineOk: true,
      runIds: [] as string[],
      poIds: [] as string[],
      invoice: "",
    };
    /** A transfer pays things in its own currency only. */
    const otherCurrency = (label: string, c: string) =>
      c.toUpperCase() !== cur.toUpperCase() ? `${label} is in ${c.toUpperCase()}; this transfer is ${cur.toUpperCase()}` : null;
    if (x.kind === "bill") {
      const b = ix.billById.get(x.id);
      if (!b) return { ...r, problem: "A bill you picked no longer exists" };
      const st = billState(ix, b.id);
      const links = billLinks(ix, b.id);
      const label = b.number;
      const problem = b.status === "void" ? `${b.number} is void` : st.due <= 0 ? `${b.number} has nothing left to pay` : otherCurrency(b.number, b.currency);
      return {
        ...r,
        label,
        vendorId: b.vendor_id,
        due: st.due,
        lineOk: (b.status === "draft" || b.status === "approved") && !st.payments.some((p) => LIVE_PAYMENT.has(p.payment.status)),
        runIds: links.runIds,
        poIds: links.poIds,
        invoice: b.invoice_number,
        problem,
      };
    }
    if (x.kind === "po" || x.kind === "po_unpriced") {
      const po = ix.poById.get(x.id);
      if (!po) return { ...r, problem: "A PO you picked no longer exists" };
      const base = {
        ...r,
        label: po.number,
        vendorId: po.vendor_id,
        runIds: po.production_run_id ? [po.production_run_id] : [],
        poIds: [po.id],
        invoice: po.vendor_ref || po.number,
      };
      const wrongCurrency = otherCurrency(po.number, po.currency);
      if (wrongCurrency) return { ...base, problem: wrongCurrency };
      if (x.kind === "po") {
        const draft = billFromPo(ix, po.id, x.part ?? "balance", dayOf);
        return {
          ...base,
          label: x.part === "deposit" ? `${po.number}'s deposit` : po.number,
          due: draft.problem ? null : draft.amount,
          problem: draft.problem,
        };
      }
      const depositPct = Number(po.deposit_pct) || 0;
      const pls = ix.poLinesByPo.get(po.id) ?? [];
      const open = pls.filter((l) => l.unit_price == null);
      const priced = poTotal(ix, po.id).total;
      const problem =
        po.status === "draft" || po.status === "cancelled"
          ? `${po.number} is ${po.status === "draft" ? "still a draft" : "cancelled"}`
          : pls.length === 0
            ? `${po.number} has no lines yet: add what it bought first`
            : open.length === 0
              ? `${po.number} has its prices already: pay it as a PO`
              : open.length > 1
                ? `Set ${po.number}'s prices first`
                : depositPct > 0
                  ? `${po.number} takes a ${depositPct}% deposit: set its prices first`
                  : (ix.linesByPo.get(po.id) ?? []).length
                    ? `Set ${po.number}'s price first: it's billed already`
                    : !(Number(open[0].qty) > 0)
                      ? `${po.number}'s line has no quantity`
                      : cents(x.amount) <= cents(priced)
                        ? `${po.number}'s priced lines come to ${money(priced)}: the payment has to be more to price the rest`
                        : null;
      if (!problem) {
        const l = open[0];
        const forLine = mulRound(x.amount - priced, 1, 2);
        const qty = Number(l.qty);
        const price = price4(forLine, qty);
        const rest = mulRound(forLine - mulRound(qty, price, 2), 1, 2);
        prices[idx] = { lineId: l.id, price, qty, forLine, label: l.sku || l.name };
        if (cents(Math.abs(rest)) > cents(ROUNDING_MAX))
          return { ...base, problem: `${money(forLine)} for ${qtyText(qty)} isn't a price to 4 places: set ${po.number}'s price by hand` };
        if (cents(rest) !== 0)
          lines[idx].push({
            kind: "rounding",
            amount: rest,
            description: `${l.sku || l.name} at ${priceText(price, cur)} × ${qtyText(qty)}: ${money(rest)} to the cent`,
            adjustment: null,
            runId: null,
            invoice: "",
            poId: null,
          });
      }
      return { ...base, problem };
    }
    if (x.kind === "production_cost") {
      const c = ix.costById.get(x.id);
      if (!c) return { ...r, problem: "A run cost you picked no longer exists" };
      const label = `"${c.description || c.invoice_number || "The run's cost"}"`;
      const on = ix.linesByCost.get(c.id)?.[0];
      const elsewhere = paidElsewhere(paidOf(c), t).elsewhere;
      const forT = amountFor(c, cur);
      return {
        ...r,
        label,
        vendorId: c.vendor_id ?? (c.po_id ? (ix.poById.get(c.po_id)?.vendor_id ?? null) : null),
        due: forT,
        runIds: [c.run_id],
        poIds: c.po_id ? [c.po_id] : [],
        invoice: c.invoice_number,
        problem: on
          ? `${label} is on ${ix.billById.get(on.bill_id)?.number ?? "a bill"} already: pick that bill`
          : c.basis !== "one_time"
            ? `${label} is per unit: bill it by hand`
            : forT == null
              ? otherCurrency(label, c.currency)
              : elsewhere
                ? `${label}: ${elsewhere}, not by this transfer`
                : null,
      };
    }
    const tool = ix.toolingById.get(x.id);
    if (!tool) return { ...r, problem: "A tooling item you picked no longer exists" };
    const on = ix.linesByTooling.get(tool.id)?.[0];
    const elsewhere = paidElsewhere(paidOf(tool), t).elsewhere;
    const forT = amountFor(tool, cur);
    return {
      ...r,
      label: tool.name,
      vendorId: tool.vendor_id,
      due: forT,
      runIds: tool.paid_by_run_id ? [tool.paid_by_run_id] : [],
      invoice: tool.invoice_number,
      problem: on
        ? `${tool.name} is on ${ix.billById.get(on.bill_id)?.number ?? "a bill"} already: pick that bill`
        : forT == null
          ? otherCurrency(tool.name, tool.currency)
          : elsewhere
            ? `${tool.name}: ${elsewhere}, not by this transfer`
            : null,
    };
  });
  for (const [i, x] of input.targets.entries()) {
    const it = info[i];
    if (it.problem) problems.push(it.problem);
    else if (!(cents(x.amount) > 0)) problems.push(`${it.label}: the amount is ${money(x.amount || 0)}`);
    else if (it.due != null && cents(x.amount) > cents(it.due)) problems.push(`${it.label} has ${money(it.due)} left to pay`);
    else if (it.due != null && cents(x.amount) < cents(it.due)) keeps.push({ label: it.label, left: mulRound(it.due - x.amount, 1, 2) });
  }
  // Never twice, and never a PO with its own bill (the bill is the PO).
  const ids = input.targets.map((x) => (x.kind === "po" || x.kind === "po_unpriced" ? `po:${x.id}` : `${x.kind}:${x.id}`));
  const twice = ids.findIndex((k, i) => ids.indexOf(k) !== i);
  if (twice >= 0) problems.push(`${info[twice].label} is picked twice`);
  for (const [i, x] of input.targets.entries())
    if (x.kind === "bill") {
      const po = input.targets.find((y) => (y.kind === "po" || y.kind === "po_unpriced") && info[i].poIds.includes(y.id));
      if (po) problems.push(`${info[i].label} is ${ix.poById.get(po.id)?.number}'s own bill: pick one or the other`);
    }
  // One vendor, and the one the transfer went to.
  const vendors = [...new Set(info.map((x) => x.vendorId).filter((v): v is string => !!v))];
  const went = transferVendor(ix, t);
  if (vendors.length > 1) problems.push("One transfer went to one vendor: pick things of one vendor");
  const vendorId = vendors[0] ?? went?.vendorId ?? null;
  if (vendors.length === 1 && went && went.vendorId !== vendorId) {
    const payeeIsIt = !!t.payee_name && namesMatch(t.payee_name, ix.vendorById.get(vendorId as string) as Vendor);
    if (went.how === "recipient" || !payeeIsIt)
      problems.push(
        `Transfer went to ${vendorLabelOf(ix.vendorById.get(went.vendorId))}; what you picked is ${vendorLabelOf(ix.vendorById.get(vendorId as string))}'s`,
      );
  }
  if (!vendorId && input.targets.length) problems.push(`Set the vendor on ${info[0].label} first: a bill needs one`);
  // Adjustments: where each goes.
  const largest = (ok: (i: number) => boolean) =>
    input.targets
      .map((x, i) => ({ i, amount: x.amount }))
      .filter(({ i }) => info[i].lineOk && ok(i))
      .sort((a, b) => b.amount - a.amount)[0]?.i;
  const full = (i: number) =>
    input.targets[i].kind === "po_unpriced" || (info[i].due != null && cents(input.targets[i].amount) === cents(info[i].due as number));
  if (adjustments.filter((a) => a.kind === "rounding").length > 1) problems.push("The vendor's rounding is one amount");
  for (const [j, a] of adjustments.entries()) {
    if (a.kind === "rounding") {
      if (cents(a.amount) === 0) continue;
      if (cents(Math.abs(a.amount)) > cents(ROUNDING_MAX)) {
        problems.push(
          `A vendor's rounding is ${money(ROUNDING_MAX)} at most; this is ${money(Math.abs(a.amount))}: say what the rest was, or pay part of something`,
        );
        continue;
      }
      const i = largest(full);
      if (i == null) {
        problems.push("The vendor's rounding goes on something paid in full that has no other payment yet");
        continue;
      }
      lines[i].push({ kind: "rounding", amount: a.amount, description: roundingText(a.amount, cur), adjustment: j, runId: null, invoice: "", poId: null });
      continue;
    }
    if (!(cents(a.amount) > 0)) {
      problems.push(`${ADJUSTMENT_LABELS[a.kind]}: an extra is more than ${money(0)}`);
      continue;
    }
    if (a.run_id && cur.toUpperCase() !== "USD") problems.push(`A run cost from a transfer in ${cur} needs its rate: enter it on the run by hand`);
    const i = largest((k) => !a.run_id || info[k].runIds.includes(a.run_id));
    if (i == null) {
      problems.push(a.run_id ? "Nothing you picked is on that production run" : "The extra goes on something with no other payment yet");
      continue;
    }
    const x = input.targets[i];
    lines[i].push({
      kind: "charge",
      amount: a.amount,
      description: a.description.trim() || ADJUSTMENT_LABELS[a.kind],
      adjustment: j,
      runId: a.run_id ?? null,
      invoice: info[i].invoice,
      poId: x.kind === "po" || x.kind === "po_unpriced" ? x.id : (info[i].poIds[0] ?? null),
    });
  }
  // A bill's rounding all told (the vendor's, a price's cents) is ROUNDING_MAX at most.
  for (const [i, ls] of lines.entries()) {
    const x = input.targets[i];
    const already = x.kind === "bill" ? (ix.linesByBill.get(x.id) ?? []).filter((l) => l.kind === "rounding").map((l) => Number(l.amount)) : [];
    if (!ls.some((l) => l.kind === "rounding")) continue;
    const r = add(...already, ...ls.filter((l) => l.kind === "rounding").map((l) => l.amount));
    if (cents(Math.abs(r)) > cents(ROUNDING_MAX))
      problems.push(`The rounding on ${info[i].label} comes to ${money(r)}: more than the ${money(ROUNDING_MAX)} a vendor's rounding can be`);
  }
  const total = add(...input.targets.map((x) => x.amount), ...adjustments.map((a) => a.amount));
  if (input.targets.length && cents(total) !== cents(t.amount))
    problems.push(`What you picked adds up to ${money(total)}; the transfer was ${money(t.amount)}`);
  const pays = input.targets.map((x, i) => add(x.amount, ...lines[i].filter((l) => l.adjustment != null).map((l) => l.amount)));
  return { problems, vendorId, total, lines, pays, prices, keeps };
}

// Wise statement CSV ----------------------------------------------------------------------------------------------

/** RFC 4180 CSV (quotes, doubled quotes, commas and line breaks inside quotes). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  const s = text.replace(/^﻿/, "");
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (quoted) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else if (c !== "\r") field += c;
  }
  if (field || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((f) => f.trim() !== ""));
}

export type StatementTransfer = {
  wise_transfer_id: number;
  transfer_date: string;
  amount: number;
  currency: string;
  fee: number;
  reference: string;
  payee_name: string;
  description: string;
  note: string;
};

/**
 * The transfers out of a Wise statement CSV (Wise → Statements → CSV): one per "TRANSFER-<id>" debit, the amount the
 * payee got (Wise lists its fee as a separate FEE- row; "Total fees" is read from the transfer's own row). Columns are
 * read by name, and only the ones named here: "Payee Account Number" is never read.
 */
export function parseStatement(text: string): { rows: StatementTransfer[]; skipped: Record<string, number>; problem: string | null } {
  const csv = parseCsv(text);
  const header = (csv[0] ?? []).map((h) => h.trim());
  const col = (name: string) => header.indexOf(name);
  const need = ["TransferWise ID", "Date", "Amount", "Currency"];
  const missing = need.filter((n) => col(n) < 0);
  if (missing.length) return { rows: [], skipped: {}, problem: `This doesn't look like a Wise statement CSV (no ${missing.join(", ")} column)` };
  const at = {
    id: col("TransferWise ID"),
    date: col("Date"),
    amount: col("Amount"),
    currency: col("Currency"),
    description: col("Description"),
    reference: col("Payment Reference"),
    payee: col("Payee Name"),
    fees: col("Total fees"),
    note: col("Note"),
  };
  const get = (r: string[], i: number) => (i >= 0 ? (r[i] ?? "").trim() : "");
  const rows: StatementTransfer[] = [];
  const skipped: Record<string, number> = {};
  const skip = (why: string) => {
    skipped[why] = (skipped[why] ?? 0) + 1;
  };
  for (const r of csv.slice(1)) {
    const id = get(r, at.id);
    const m = id.match(/^TRANSFER-(\d+)$/);
    if (!m) {
      skip(
        id.startsWith("FEE-") ? "fees (read from their transfer)" : /^BALANCE|CARD|DEPOSIT/.test(id) ? "conversions, interest, deposits, card" : "other rows",
      );
      continue;
    }
    const amount = Number(get(r, at.amount).replace(/,/g, ""));
    if (!(amount < 0)) {
      skip("money in (not a payment)");
      continue;
    }
    const d = get(r, at.date).match(/^(\d{2})-(\d{2})-(\d{4})$/);
    rows.push({
      wise_transfer_id: Number(m[1]),
      transfer_date: d ? `${d[3]}-${d[2]}-${d[1]}` : get(r, at.date).slice(0, 10),
      amount: Math.abs(amount),
      currency: get(r, at.currency).toUpperCase() || "USD",
      fee: Math.abs(Number(get(r, at.fees).replace(/,/g, "")) || 0),
      reference: get(r, at.reference),
      payee_name: get(r, at.payee),
      description: get(r, at.description),
      note: get(r, at.note),
    });
  }
  return { rows, skipped, problem: null };
}

// Bookkeeper pack ---------------------------------------------------------------------------------------------------

/** "2026-10" → its first and last day. */
export function monthRange(month: string) {
  const [y, m] = month.split("-").map(Number);
  const from = `${y}-${String(m).padStart(2, "0")}-01`;
  const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`;
  return { from, to: addDays(next, -1) };
}

/** A file name safe on Windows and in a zip: letters, digits, spaces and - _ . ( ) only. */
export const safeName = (s: string) =>
  s
    .replace(/[^\p{L}\p{N} ._()-]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);

// CSV cells with the formula guard: shared/csv.ts (every table's Copy and CSV use it too).
export { csvCell, toCsv } from "./csv.ts";

const extOf = (filename: string, type: string) => {
  const m = filename.match(/\.([A-Za-z0-9]{1,5})$/);
  if (m) return m[1].toLowerCase();
  return type === "application/pdf" ? "pdf" : type.startsWith("image/") ? type.slice(6).replace("jpeg", "jpg") : "bin";
};

export type PackBill = {
  bill: Bill;
  vendor: string;
  pos: string[];
  runs: string[];
  paid: number;
  owed: number;
  files: string[];
};
export type PackPayment = {
  payment: Payment;
  vendor: string;
  bills: { number: string; invoice: string; amount: number }[];
  files: string[];
};
export type BookkeeperPack = {
  month: string;
  from: string;
  to: string;
  bills: PackBill[];
  payments: PackPayment[];
  /** Every file in the zip: an attachment and where it goes. */
  files: { attachmentId: string; path: string }[];
  missing: { invoices: string[]; receipts: string[] };
  draftsLeftOut: number;
  /** Wise transfers sent in the month and not confirmed yet: payments.csv leaves them out until they are. */
  unconfirmed: UnconfirmedTransfers;
  totals: { billed: Record<string, number>; paid: Record<string, number>; fees: Record<string, number> };
  csv: { bills: string; payments: string; presales?: string; presaleShipments?: string };
  /** Pre-sales --- the month's income entries and shipments to backers (shared/presales.ts presalePack()). */
  presales?: PresalePack;
};

/** `notLooked`: sent, but Wise history never got to suggest a match (still "Not sent yet" from before it went). */
export type UnconfirmedTransfers = {
  count: number;
  toConfirm: number;
  noMatch: number;
  notLooked: number;
  onTheirWay: number;
  totals: Record<string, number>;
};

/**
 * Wise transfers dated in the month that aren't payments yet, so the month's payments.csv is short by them (a payment
 * linked later is dated the transfer's day): sent and not confirmed (Wise history's To confirm and No match), and still
 * on their way (being funded or processed, or bounced and held by Wise until it sends them again). Cancelled, refunded or
 * charged-back ones moved no money in the end and don't count, whatever their match status.
 */
export function unconfirmedTransfers(
  transfers: readonly Pick<WiseTransfer, "transfer_date" | "match_status" | "state" | "amount" | "currency">[],
  month: string,
): UnconfirmedTransfers {
  const { from, to } = monthRange(month);
  const out: UnconfirmedTransfers = { count: 0, toConfirm: 0, noMatch: 0, notLooked: 0, onTheirWay: 0, totals: {} };
  for (const t of transfers) {
    if (t.match_status !== "suggested" && t.match_status !== "no_match" && t.match_status !== "new") continue;
    const day = t.transfer_date ? String(t.transfer_date).slice(0, 10) : null;
    if (!day || day < from || day > to) continue;
    const status = statusForWiseState(t.state);
    const sent = status === "paid";
    if (!sent && status !== "processing" && status !== "awaiting_funding") continue; // cancelled or refunded
    out.count++;
    if (!sent) out.onTheirWay++;
    else if (t.match_status === "suggested") out.toConfirm++;
    else if (t.match_status === "no_match") out.noMatch++;
    else out.notLooked++;
    out.totals[t.currency] = add(out.totals[t.currency] ?? 0, Number(t.amount));
  }
  return out;
}

/** "3 Wise transfers in September ($18,842.50) aren't confirmed yet, 1 of them still on its way", or null when none. */
export function unconfirmedText(u: UnconfirmedTransfers, monthName: string): string | null {
  if (!u.count) return null;
  const money = Object.entries(u.totals)
    .map(([cur, n]) => moneyText(n, cur))
    .join(" + ");
  const way = !u.onTheirWay
    ? ""
    : u.onTheirWay === u.count
      ? ` (still on ${u.count === 1 ? "its" : "their"} way)`
      : `, ${u.onTheirWay} of them still on ${u.onTheirWay === 1 ? "its" : "their"} way`;
  return `${u.count === 1 ? "1 Wise transfer" : `${u.count} Wise transfers`} in ${monthName} (${money}) ${u.count === 1 ? "isn't" : "aren't"} confirmed yet${way}`;
}

/**
 * The month's bookkeeper pack: bills dated in the month (approved, scheduled or paid; drafts and voids left out) and
 * payments made in it, as two CSVs, with the bills' invoice files and the payments' receipts named so they sort by
 * date and say what they are. The zip (server/src/bookkeeper.ts), the Bookkeeper pack page and the MCP all use this.
 */
export function bookkeeperPack(
  ix: BillIndex,
  month: string,
  dayOf: (ts: string) => string,
  presales?: PresalePack,
  transfers: Parameters<typeof unconfirmedTransfers>[0] = [],
): BookkeeperPack {
  const { from, to } = monthRange(month);
  const inMonth = (d: string | null) => !!d && d >= from && d <= to;
  const files: BookkeeperPack["files"] = [];
  const used = new Set<string>();
  const place = (dir: string, base: string, a: { id: string; filename: string; content_type: string }) => {
    const ext = extOf(a.filename, a.content_type);
    let name = `${dir}/${safeName(base)}.${ext}`;
    for (let n = 2; used.has(name.toLowerCase()); n++) name = `${dir}/${safeName(base)} (${n}).${ext}`;
    used.add(name.toLowerCase());
    files.push({ attachmentId: a.id, path: name });
    return name.slice(dir.length + 1);
  };
  const tally = (m: Record<string, number>, cur: string, n: number) => {
    m[cur] = add(m[cur] ?? 0, n);
  };
  const totals: BookkeeperPack["totals"] = { billed: {}, paid: {}, fees: {} };
  let draftsLeftOut = 0;
  const bills: PackBill[] = [];
  const billDay = (b: Bill) => (b.bill_date ? String(b.bill_date).slice(0, 10) : dayOf(b.created_at));
  for (const b of [...ix.d.bills].sort((x, y) => billDay(x).localeCompare(billDay(y)) || x.number.localeCompare(y.number))) {
    const day = billDay(b);
    if (!inMonth(day)) continue;
    if (b.status === "draft") {
      draftsLeftOut++;
      continue;
    }
    if (b.status === "void") continue;
    const st = billState(ix, b.id);
    const links = billLinks(ix, b.id);
    const vendor = vendorLabelOf(ix.vendorById.get(b.vendor_id));
    const invoices = (ix.filesByOwner.get(`bill:${b.id}`) ?? []).filter((a) => a.kind !== "receipt");
    const names = invoices.map((a) => place("bills", `${day} ${vendor} ${b.number}${b.invoice_number ? ` ${b.invoice_number}` : ""}`, a));
    tally(totals.billed, b.currency, Number(b.amount));
    bills.push({
      bill: b,
      vendor,
      pos: links.poIds.map((id) => ix.poById.get(id)?.number ?? "").filter(Boolean),
      runs: links.runIds.map((id) => ix.runById.get(id)?.number ?? "").filter(Boolean),
      paid: st.paid,
      owed: add(st.due, st.pending),
      files: names,
    });
  }
  const payments: PackPayment[] = [];
  for (const p of [...ix.d.payments].sort((x, y) => String(x.paid_on).localeCompare(String(y.paid_on)) || x.number.localeCompare(y.number))) {
    if (p.status !== "paid" || !inMonth(p.paid_on ? String(p.paid_on).slice(0, 10) : null)) continue;
    const vendor = vendorLabelOf(ix.vendorById.get(p.vendor_id ?? ""));
    const day = String(p.paid_on).slice(0, 10);
    // Receipts dropped on a Wise history transfer before it was linked are copied onto its payment when it's linked.
    const receipts = ix.filesByOwner.get(`payment:${p.id}`) ?? [];
    const via = p.method === "wise" ? `Wise${p.wise_transfer_id ? ` ${p.wise_transfer_id}` : ""}` : PAYMENT_METHOD_LABELS[p.method];
    const names = receipts.map((a) => place("receipts", `${day} ${vendor} ${p.number} ${via}`, a));
    tally(totals.paid, p.currency, Number(p.amount));
    if (Number(p.fee) > 0) tally(totals.fees, p.fee_currency || p.currency, Number(p.fee));
    payments.push({
      payment: p,
      vendor,
      bills: (ix.allocsByPayment.get(p.id) ?? []).map((a) => {
        const b = ix.billById.get(a.bill_id);
        return { number: b?.number ?? "", invoice: b?.invoice_number ?? "", amount: Number(a.amount) };
      }),
      files: names,
    });
  }
  const billsCsv = toCsv([
    [
      "Bill",
      "Vendor",
      "Vendor's invoice no.",
      "Bill date",
      "Due date",
      "Kind",
      "Currency",
      "Amount",
      "Paid",
      "Still owed",
      "Status",
      "POs",
      "Production runs",
      "Description",
      "Invoice file",
    ],
    ...bills.map((r) => [
      r.bill.number,
      r.vendor,
      r.bill.invoice_number,
      r.bill.bill_date ? String(r.bill.bill_date).slice(0, 10) : "",
      r.bill.due_date ? String(r.bill.due_date).slice(0, 10) : "",
      BILL_KIND_LABELS[r.bill.kind],
      r.bill.currency,
      Number(r.bill.amount).toFixed(2),
      r.paid.toFixed(2),
      r.owed.toFixed(2),
      BILL_STATUS_LABELS[r.bill.status],
      r.pos.join(" "),
      r.runs.join(" "),
      r.bill.description,
      r.files.length ? r.files.map((f) => `bills/${f}`).join("; ") : "MISSING",
    ]),
  ]);
  const paymentsCsv = toCsv([
    [
      "Payment",
      "Paid on",
      "Vendor",
      "Method",
      "Paid with", // Which card ---
      "Reference the vendor saw",
      "Wise transfer",
      "Currency",
      "Amount",
      "Fee",
      "Total out",
      "Bills paid",
      "Receipt file",
    ],
    ...payments.map((r) => [
      r.payment.number,
      String(r.payment.paid_on).slice(0, 10),
      r.vendor,
      PAYMENT_METHOD_LABELS[r.payment.method],
      paymentWith(ix, r.payment),
      r.payment.reference,
      r.payment.wise_transfer_id ?? "",
      r.payment.currency,
      Number(r.payment.amount).toFixed(2),
      Number(r.payment.fee).toFixed(2),
      add(Number(r.payment.amount), Number(r.payment.fee)).toFixed(2),
      r.bills.map((b) => `${b.number}${b.invoice ? ` (${b.invoice})` : ""} ${b.amount.toFixed(2)}`).join("; "),
      r.files.length ? r.files.map((f) => `receipts/${f}`).join("; ") : "MISSING",
    ]),
  ]);
  // Pre-sales --- customer deposits in (income entries) and what's shipped (deposits to recognise), with the statements.
  let presaleCsv: { presales?: string; presaleShipments?: string } = {};
  if (presales) {
    const statement = new Map<string, string[]>();
    for (const x of presales.income)
      statement.set(
        x.entry.id,
        x.files.map(
          (a) =>
            `presales/${place("presales", `${String(x.entry.received_on).slice(0, 10)} ${x.presale.name} ${x.entry.source} ${x.entry.kind}${x.entry.reference ? ` ${x.entry.reference}` : ""}`, a)}`,
        ),
      );
    // The share of deposits needs the pre-sale's total pledge value: without one anywhere, no column (the README says so).
    const withShare = presales.shipments.some((s) => s.depositShareCents != null);
    presaleCsv = {
      presales: toCsv([
        [
          "Pre-sale",
          "Run",
          "Date",
          "Source",
          "What",
          "Description",
          "Reference",
          "Currency",
          "Amount",
          "USD rate",
          "USD (in +, out −)",
          "Note",
          "Statement file",
        ],
        ...presales.income.map((x) => [
          x.presale.name,
          x.run,
          String(x.entry.received_on).slice(0, 10),
          x.entry.source,
          x.entry.kind,
          x.entry.description,
          x.entry.reference,
          x.entry.currency,
          Number(x.entry.amount).toFixed(2),
          Number(x.entry.usd_rate),
          (x.cents / 100).toFixed(2),
          x.entry.note,
          statement.get(x.entry.id)?.join("; ") || "",
        ]),
      ]),
      presaleShipments: toCsv([
        [
          "Pre-sale",
          "Run",
          "Shipped on",
          "Region",
          "Packages",
          "Units",
          "Units by item",
          "Pledge value (USD)",
          ...(withShare ? ["Share of deposits (USD)"] : []),
          "Units at landed cost (USD)",
          "Units with no cost yet",
          "Postage (USD)",
          "DDP (USD)",
          "Packing fee (USD)",
          "Freight to Philadelphia (USD)",
          "Other shipping (USD)",
          "Shipping total (USD)",
          "Shipping already on bills",
          "Filled in later",
          "Note",
        ],
        ...presales.shipments.map((s) => [
          s.presale.name,
          s.run,
          String(s.shipment.shipped_on).slice(0, 10),
          s.shipment.region,
          s.shipment.packages,
          s.units,
          s.byItem.map((x) => `${x.sku} × ${x.qty}`).join("; "),
          Number(s.shipment.pledge_value).toFixed(2),
          ...(withShare ? [s.depositShareCents == null ? "" : (s.depositShareCents / 100).toFixed(2)] : []),
          (s.unitCostCents / 100).toFixed(2),
          s.missingCost.join(", "),
          (s.costs.postage / 100).toFixed(2),
          (s.costs.ddp / 100).toFixed(2),
          (s.costs.packing / 100).toFixed(2),
          (s.costs.freight / 100).toFixed(2),
          (s.costs.other / 100).toFixed(2),
          (s.shippingCents / 100).toFixed(2),
          s.bills.join("; "),
          s.filledLater.join("; "),
          s.shipment.note,
        ]),
      ]),
    };
  }
  return {
    month,
    from,
    to,
    bills,
    payments,
    files,
    ...(presales ? { presales } : {}),
    missing: {
      invoices: bills.filter((b) => !b.files.length).map((b) => `${b.bill.number} ${b.vendor}${b.bill.invoice_number ? ` ${b.bill.invoice_number}` : ""}`),
      receipts: payments
        .filter((p) => !p.files.length)
        .map((p) => `${p.payment.number} ${p.vendor} ${moneyText(Number(p.payment.amount), p.payment.currency)}`),
    },
    draftsLeftOut,
    unconfirmed: unconfirmedTransfers(transfers, month),
    totals,
    csv: { bills: billsCsv, payments: paymentsCsv, ...presaleCsv },
  };
}

/** The pack in a few numbers, for summary.json, the MCP and the page. */
export function packSummary(p: BookkeeperPack) {
  return {
    month: p.month,
    period: { from: p.from, to: p.to },
    bills: { count: p.bills.length, total: p.totals.billed, missing_invoice_files: p.missing.invoices, drafts_left_out: p.draftsLeftOut },
    payments: {
      count: p.payments.length,
      total: p.totals.paid,
      fees: p.totals.fees,
      // Not in payments.csv until confirmed in Wise history: a pack with any is incomplete.
      unconfirmed_wise_transfers: { count: p.unconfirmed.count, still_on_their_way: p.unconfirmed.onTheirWay, total: p.unconfirmed.totals },
      by_method: Object.fromEntries(PAYMENT_METHODS.map((m) => [m, p.payments.filter((x) => x.payment.method === m).length] as const).filter(([, n]) => n > 0)),
      missing_receipts: p.missing.receipts,
    },
    // Pre-sales --- customer deposits in, and the pledge value of what shipped (to recognise as income)
    ...(p.presales
      ? {
          presales: {
            income_entries: p.presales.income.length,
            money_in_usd: p.presales.totals.inCents / 100,
            money_out_usd: p.presales.totals.outCents / 100,
            shipments: p.presales.shipments.length,
            pledge_value_shipped_usd: p.presales.totals.pledgeCents / 100,
            share_of_deposits_shipped_usd: p.presales.shipments.some((s) => s.depositShareCents != null) ? p.presales.totals.depositShareCents / 100 : null,
            no_total_pledge_value: p.presales.unknownPledge,
            units_shipped_at_landed_cost_usd: p.presales.totals.unitCostCents / 100,
            shipping_to_backers_usd: p.presales.totals.shippingCents / 100,
          },
        }
      : {}),
    files: p.files.map((f) => f.path),
  };
}

/**
 * Which card --- how a payment was made, in words: "Amex Business ••1005 · The vendor's website", "Wise", or the method
 * alone for a payment that doesn't say (migration 073).
 */
export function paymentWith(
  ix: { payAccountById: Map<string, { name: string; last4: string }> },
  p: Pick<Payment, "method" | "paid_via"> & { pay_account_id?: string | null },
) {
  const account = p.pay_account_id ? ix.payAccountById.get(p.pay_account_id) : null;
  return paidWithText({ account, via: p.paid_via, method: PAYMENT_METHOD_LABELS[p.method] });
}

/**
 * Accounted for --- how much of a PO is paid and by what (Mitch, 2026-11-03: "ensure that payments are accounted for and
 * linked"): its deposit and balance paid (`poBillPlan()`), and each payment that went, in words ("Wise · Aug 5",
 * "Amex Business ••1005 · The vendor's website · Oct 16"). `state`: paid in full, part paid, billed and not paid yet,
 * or nothing billed (a PO nobody has said how it was paid).
 */
export function poPaid(ix: BillIndex, poId: string, dayOf: (ts: string) => string) {
  const plan = poBillPlan(ix, poId, dayOf);
  if (!plan) return null;
  const paid = add(plan.deposit.paid, plan.balance.paid);
  const seen = new Set<string>();
  const payments: { number: string; how: string; on: string | null }[] = [];
  for (const b of plan.bills)
    for (const a of ix.allocsByBill.get(b.id) ?? []) {
      const p = ix.paymentById.get(a.payment_id);
      if (!p || p.status !== "paid" || seen.has(p.id)) continue;
      seen.add(p.id);
      payments.push({ number: p.number, how: paymentWith(ix, p), on: p.paid_on ? String(p.paid_on).slice(0, 10) : null });
    }
  const state: "paid" | "part" | "billed" | "none" =
    plan.total > 0 && cents(paid) >= cents(plan.total) ? "paid" : paid > 0 ? "part" : plan.bills.length ? "billed" : "none";
  return { total: plan.total, unpriced: plan.unpriced, paid, state, payments, currency: plan.po.currency };
}
